import { useCallback, useRef, useState } from 'react';

const ATTACHMENT_FILE_ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';
const ALLOWED_ATTACHMENT_FILE_EXTENSIONS = new Set(['pdf', 'jpg', 'jpeg', 'png']);
const MAX_ATTACHMENT_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const MAX_FILES_PER_ATTACHMENT = 10;

const validateAttachmentFile = (file) => {
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  if (!ALLOWED_ATTACHMENT_FILE_EXTENSIONS.has(ext)) {
    return '添付ファイルはPDF、JPG、JPEG、PNG形式を選択してください。';
  }
  if (file.size > MAX_ATTACHMENT_FILE_SIZE_BYTES) {
    return '添付ファイルは1ファイル5MB以下を選択してください。';
  }
  return null;
};

const isPdfFile = (name) => /\.pdf$/i.test(String(name || ''));

const TOAST_TYPE_STYLES = {
  success: 'bg-gray-900 text-white',
  error: 'bg-gray-900 text-white',
  warning: 'bg-gray-900 text-white',
  info: 'bg-gray-900 text-white',
};

const TOAST_ICONS = {
  success: '✓',
  error: '✕',
  warning: '⚠',
  info: 'ℹ',
};

const TOAST_MAX_VISIBLE = 3;

const APP_DOC_ATTACHMENT_DEFS = [
      // 表裏がある書類は表面・裏面に分割
      { key: 'mynumber', label: 'マイナンバーカードの写し', parts: [{ key: 'front', label: '表面' }, { key: 'back', label: '裏面' }] },
      { key: 'license', label: '運転免許証の写し', parts: [{ key: 'front', label: '表面' }, { key: 'back', label: '裏面' }] },
      { key: 'residenceCard', label: '在留カードの写し', parts: [{ key: 'front', label: '表面' }, { key: 'back', label: '裏面' }] },
      { key: 'disabilityBook', label: '障害者手帳の写し', parts: [{ key: 'front', label: '表面' }, { key: 'back', label: '裏面' }] },
      { key: 'passport', label: 'パスポートの写し' },
      { key: 'kosekiAll', label: '全世帯員の戸籍謄本の写し（全部事項証明書）' },
      { key: 'juminhyoForeign', label: '全世帯員の住民票の写し（外国人の場合）' },
      { key: 'protectionCertificate', label: '保護受給証明書\n（給付状態次第では戸籍謄本や住民票の代替として利用可。詳細は末尾の「申出に当たってのチェックリスト」を参照。）' },
      { key: 'bankBookOrCard', label: '申出者本人の振込先口座が確認できる預貯金通帳の写し、またはキャッシュカードの写し' },
      // 「必要に応じて提出いただく資料」も入力枠を用意（任意）
      { key: 'kosekiFupyo', label: '戸籍謄本の附票の写し（当時の居所を記載できない場合）' },
      { key: 'proxyDocs', label: '代理人関係書類（委任状等）' },
    ];

    const ATTACHMENT_REQUIRED_MESSAGES = {
      kosekiAll: '全世帯員の戸籍謄本の写し（全部事項証明書）を添付してください。',
    };

    const APP_DOC_ADDON_TYPES = [
      '入院患者日用品費',
      '救護施設等基準生活費',
      '介護施設入所者基本生活費等',
      '障碍者加算',
      '母子加算',
      '妊産婦加算',
      '在宅患者加算',
      '放射線障害者加算',
      '未成年者控除（20歳未満控除）',
    ];

    const RELATION_TO_HEAD_OPTIONS = [
      '世帯主本人',
      '配偶者',
      '子',
      '父',
      '母',
      '孫',
      '祖父',
      '祖母',
      '兄',
      '弟',
      '姉',
      '妹',
      '甥',
      '姪',
      'ひ孫',
      'その他',
    ];

    const MEMBER_INSTRUCTION =
      '平成 25 年８月から令和８年３月までの期間において、保護を受給していた世帯員すべてを記載してください。申出日時点で死亡している世帯員については「死亡」欄にチェックを入れてください';

    const MEMBER_FOOTNOTES = (
      <p className="text-sm text-gray-800 leading-relaxed mt-6 whitespace-pre-line">
        {`※ 本申出は、申出先の自治体で保護を受給されていた期間の世帯主が行うものとなります。世帯主が現在死亡されている場合は、留意事項の※２を参照してください。
※ 申出先の自治体で保護を受給されていた期間について、記入してください。
 （他の自治体で保護を受給されていた期間がある場合は、別途その自治体へ申出が必要となります。また、申出先の自治体で複数回保護を受給されている場合は、それぞれの期間ごとに申出が必要となります。）
※ 「生活保護受給期間」について、大学就学等により、世帯分離となっている期間を除いてください。`}
      </p>
    );

    const RESIDENCE_FOOTNOTES = (
      <div className="mt-4 space-y-2 text-sm text-gray-800">
        <p>※ 居所には、当時住んでいた場所を記載ください。</p>
        <p>※ 当時の居所を記載できない場合は、当時の住所の履歴を確認するため、戸籍謄本の附票の写しを添付してください。</p>
      </div>
    );

    const ADDITION_INTRO = (
      <div className="text-sm text-gray-800 space-y-3 mb-4 leading-relaxed">
        <p>
          当時の最低生活費の確認のため、申出者及び世帯員について、該当する加算等を記載してください。（該当がない場合は、記載不要。）
        </p>
        <p>
          ※ 加算等の該当がある場合は、併せて挙証資料を提出してください。なお、挙証資料の提出がない場合であっても申出書を受け付けできますが、加算等について算定ができない場合があります。
        </p>
      </div>
    );

    const CLOSING_NOTES = (
      <div className="text-sm text-gray-800 space-y-2 mb-4">
        <p>申出書の記載内容は以上となります。</p>
      </div>
    );

    const evidenceExampleByCategory = (cat) => {
      const c = String(cat || '');
      if (c.includes('障碍者加算') || c.includes('障害者加算')) return '障害者手帳の写し、診断書 等';
      if (c.includes('妊産婦加算')) return '母子健康手帳の写し、診断書 等';
      if (c.includes('母子加算')) return '戸籍謄本の写し 等';
      if (c.includes('介護施設')) return '入所証明書、契約書の写し 等';
      if (c.includes('入院')) return '入院証明書、領収書の写し 等';
      if (c.includes('未成年者控除')) return '生年月日が分かる資料（戸籍等）';
      if (c.includes('在宅患者')) return '診断書、通院証明 等';
      if (c.includes('放射線')) return '該当を示す証明資料';
      return '該当を示す資料（例：証明書、診断書 等）';
    };

    const RYUI_JIKO = (
      <div className="border-2 border-dotted border-gray-400 rounded-lg p-4 md:p-5 bg-amber-50/30 text-sm text-gray-900 leading-relaxed space-y-3">
        <p className="font-bold text-center mb-2">【留意事項】</p>
        <p>
          <span className="font-semibold">※1</span>{' '}
          虚偽の申出その他不正な手段により給付金等を受けた場合には、刑法上の罪に問われることがあります。
        </p>
        <p>
          <span className="font-semibold">※2</span>{' '}
          本申出は、申出先の自治体で保護を受給されていた当時の世帯主が行うものとされます。世帯主が死亡している場合は、次の順序に従い、同一順位に複数いるときはその代表者が申し出ることができます。
          <span className="block mt-1 pl-2">
            ① 配偶者（事実上婚姻関係と同様の事情にある者を含む。）　② 子　③ 父母　④ 孫　⑤ 祖父母　⑥ 兄弟姪甥　⑦ ひ孫
          </span>
        </p>
        <p>
          <span className="font-semibold">※3</span>{' '}
          申出すべき者が申出できないときは、代理人による申出ができます。この場合、委任状を添付してください。
        </p>
        <p>
          <span className="font-semibold">※4</span>{' '}
          申出内容の確認等のため、記載の連絡先に連絡することがあります。
        </p>
      </div>
    );

    const FORM_ENTRY_NOTICE_GROUPS = [
      {
        title: '入力に必要な情報',
        items: [
          '申出者及び世帯員の氏名、フリガナ、続柄、生年月日、電話番号、メールアドレス、生活保護受給期間',
          '保護を受給していた当時の居所と期間（現住所と異なる場合）',
          '申出者本人名義の振込先口座情報',
        ],
      },
      {
        title: '準備する添付書類',
        items: [
          'ご準備いただく添付書類は、本人確認書類、口座情報確認書類、加算等に関する挙証資料、および必要に応じて戸籍関連書類等です。詳細につきましては、次ページ末尾に記載の「申出に当たってのチェックリスト」をご確認ください。',
        ],
      },
    ];

    const emptyMember = () => ({
      furigana: '',
      name: '',
      phone: '',
      relationToHead: '',
      relationToHeadOther: '',
      birthDate: '',
      benefitPeriod: '',
      benefitPeriodStartMonth: '',
      benefitPeriodEndMonth: '',
      email: '',
      death: 'blank',
    });

    const emptyHeadMember = () => ({ ...emptyMember(), relationToHead: '世帯主本人' });

    const emptyOtherHouseholdMember = () => ({ name: '', phone1: '', phone2: '', phone3: '', email: '' });

    const emptyPastAddress = () => ({
      address: '',
      period: '',
      periodStartMonth: '',
      periodEndMonth: '',
    });

    const pad2 = (value) => String(value || '').padStart(2, '0');

    const isYearMonth = (value) => /^\d{4}-\d{2}$/.test(String(value || ''));
    const isIsoDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || ''));

    const lastDateOfMonth = (yearMonth) => {
      if (!isYearMonth(yearMonth)) return '';
      const [year, month] = yearMonth.split('-').map(Number);
      const day = new Date(year, month, 0).getDate();
      return `${yearMonth}-${pad2(day)}`;
    };

    const monthRangeToPeriod = (startMonth, endMonth) =>
      isYearMonth(startMonth) && isYearMonth(endMonth)
        ? `${startMonth}-01~${lastDateOfMonth(endMonth)}`
        : '';

    const monthRangeLabel = (startMonth, endMonth, fallback = '') => {
      if (isYearMonth(startMonth) && isYearMonth(endMonth)) return `${startMonth} ～ ${endMonth}`;
      const raw = String(fallback || '').trim();
      const match = raw.match(/^(\d{4})[-/](\d{1,2})[-/]\d{1,2}\s*[~～〜]\s*(\d{4})[-/](\d{1,2})[-/]\d{1,2}$/);
      if (match) return `${match[1]}-${pad2(match[2])} ～ ${match[3]}-${pad2(match[4])}`;
      return raw;
    };

    const dateRangeToPeriod = (startDate, endDate) =>
      isIsoDate(startDate) && isIsoDate(endDate) ? `${startDate}~${endDate}` : '';

    const dateRangeLabel = (startDate, endDate, fallback = '') => {
      if (isIsoDate(startDate) && isIsoDate(endDate)) return `${startDate} ～ ${endDate}`;
      const raw = String(fallback || '').trim();
      const match = raw.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})\s*[~～〜]\s*(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
      if (match) return `${match[1]}-${pad2(match[2])}-${pad2(match[3])} ～ ${match[4]}-${pad2(match[5])}-${pad2(match[6])}`;
      return raw;
    };

    const validMonthRange = (startMonth, endMonth) =>
      isYearMonth(startMonth) && isYearMonth(endMonth) && startMonth <= endMonth;

    const isValidFurigana = (value) => /^[ァ-ヶヷ-ヺー・\u3000 ]+$/.test(String(value || '').trim());
    const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
    const digitsOnly = (value) => String(value || '').replace(/\D/g, '');
    const phoneDigitsOnly = (value) => digitsOnly(String(value || '').replace(/-/g, ''));
    const isValidPhoneNumber = (value) => /^\d{10,11}$/.test(phoneDigitsOnly(value));
    const sanitizeBankCodeInput = (value) => digitsOnly(value).slice(0, 4);
    const sanitizeBankBranchCodeInput = (value) => digitsOnly(value).slice(0, 3);
    const sanitizeBankAccountNumberInput = (value) => digitsOnly(value).slice(0, 7);
    const sanitizeBankFieldInput = (key, value) => {
      switch (key) {
        case 'bankName':
          return sanitizeBankCodeInput(value);
        case 'branchName':
          return sanitizeBankBranchCodeInput(value);
        case 'accountNumber':
          return sanitizeBankAccountNumberInput(value);
        default:
          return value;
      }
    };
    const bankCodeError = (value) => {
      const bankCode = String(value || '').trim();
      if (!bankCode) return 'required';
      if (!/^\d{4}$/.test(bankCode)) return 'format';
      return '';
    };
    const bankBranchNumberError = (value) => {
      const branch = String(value || '').trim();
      if (!branch) return 'required';
      if (!/^\d{3}$/.test(branch)) return 'format';
      return '';
    };
    const bankAccountNumberError = (value) => {
      const accountNumber = String(value || '').trim();
      if (!accountNumber) return 'required';
      if (!/^\d{1,7}$/.test(accountNumber)) return 'format';
      return '';
    };
    const bankAccountHolderError = (value) => {
      const holder = String(value || '').trim();
      if (!holder) return 'required';
      if (!/^[\uFF61-\uFF9F \u3000]+$/.test(holder)) return 'invalid';
      return '';
    };
    const otherHouseholdPhoneDigits = (row) => {
      const direct = phoneDigitsOnly(row?.phone);
      if (direct) return direct;
      return [row?.phone1, row?.phone2, row?.phone3].map(phoneDigitsOnly).join('');
    };
    const isApplicationEntryAllowedStatus = (status) => {
      const value = String(status || '').trim();
      return value === '2'
        || value === '4'
        || value === '申出書提出待'
        || value === '差戻'
        || value === 'APP_AWAITING_SUBMIT'
        || value === 'APP_REJECTED';
    };
    const APPLICATION_ENTRY_NOT_ALLOWED_MESSAGE =
      '現在のステータスでは入力できません。申出書提出待または差戻の場合のみ入力できます。';

    const bankAccountTypeLabel = (value) => {
      const raw = String(value || '').trim();
      if (/^9(?:[:：=＝]|$)/.test(raw) || raw.includes('その他')) return 'その他';
      if (/^4(?:[:：=＝]|$)/.test(raw) || raw.includes('貯蓄')) return '貯蓄';
      if (/^2(?:[:：=＝]|$)/.test(raw) || raw.includes('当座')) return '当座預金';
      if (/^1(?:[:：=＝]|$)/.test(raw) || raw.includes('普通')) return '普通預金';
      return raw;
    };

    const datePartsToIso = (y, m, d) => {
      const year = String(y || '').trim();
      const month = String(m || '').trim();
      const day = String(d || '').trim();
      return year && month && day ? `${year}-${pad2(month)}-${pad2(day)}` : '';
    };

    const consentDateValue = (consent) =>
      consent?.date || datePartsToIso(consent?.y, consent?.m, consent?.d);

    const normalizeRelationToHead = (member) => {
      const relation = String(member?.relationToHead || '').trim();
      if (relation !== 'その他') return relation;
      return String(member?.relationToHeadOther || '').trim() || 'その他';
    };

    const normalizeMemberForSubmit = (member) => ({
      furigana: member?.furigana || '',
      name: member?.name || '',
      phone: phoneDigitsOnly(member?.phone),
      relationToHead: normalizeRelationToHead(member),
      birthDate: member?.birthDate || '',
      benefitPeriod: dateRangeToPeriod(member?.benefitPeriodStartMonth, member?.benefitPeriodEndMonth) || member?.benefitPeriod || '',
      email: member?.email || '',
      death: member?.death === 'maru' ? 'maru' : 'blank',
    });

    const otherHouseholdContactsOf = (formValue) => {
      const rows = Array.isArray(formValue?.otherHouseholdMembers) && formValue.otherHouseholdMembers.length
        ? formValue.otherHouseholdMembers
        : [formValue?.otherHouseholdMember || emptyOtherHouseholdMember()];
      return rows.slice(0, 2);
    };

    const hasOtherHouseholdContactValue = (row) =>
      ['name', 'phone', 'phone1', 'phone2', 'phone3', 'email'].some((key) => String(row?.[key] || '').trim() !== '');

    const normalizeOtherHouseholdContactForSubmit = (row) => ({
      name: row?.name || '',
      phone1: phoneDigitsOnly(row?.phone1),
      phone2: phoneDigitsOnly(row?.phone2),
      phone3: phoneDigitsOnly(row?.phone3),
      phone: otherHouseholdPhoneDigits(row),
      email: row?.email || '',
    });

    const normalizePastAddressForSubmit = (row) => ({
      address: row?.address || '',
      period: monthRangeToPeriod(row?.periodStartMonth, row?.periodEndMonth) || row?.period || '',
    });

    const normalizeConsentForSubmit = (consent = {}) => {
      const date = consentDateValue(consent);
      const [y = '', m = '', d = ''] = date ? date.split('-') : [consent.y || '', consent.m || '', consent.d || ''];
      return {
        y,
        m,
        d,
        address: consent.address || '',
        name: consent.name || '',
      };
    };

    /** チェックリスト：該当時のみ（必須検証の対象外）— 外国人向け・必要に応じて提出 */
    const CHECKLIST_CONDITIONAL_KEYS = ['juminhyoForeign', 'addonEvidence', 'kosekiFupyo', 'proxyDocs'];

    /** チェックリスト：必須（入力確認へ進む条件） */
    const CHECKLIST_REQUIRED_KEYS = [
      'idPhoto',
      'kosekiAll',
      'bankProof',
      'headAtTime',
      'membersListed',
      'bankNameMatch',
      'additionDetails',
      'contactListed',
    ];

    const CHECKLIST_ALL_KEYS = [...CHECKLIST_REQUIRED_KEYS, ...CHECKLIST_CONDITIONAL_KEYS];

    const emptyRequiredDocs = () =>
      Object.fromEntries(CHECKLIST_ALL_KEYS.map((k) => [k, false]));

    const attachmentEmptyPart = () => ({ images: [] });
    const buildEmptyAttachments = () =>
      Object.fromEntries(
        APP_DOC_ATTACHMENT_DEFS.map((d) => [
          d.key,
          Object.fromEntries(((d.parts && d.parts.length ? d.parts : [{ key: 'single' }])).map((p) => [p.key, attachmentEmptyPart()])),
        ])
      );

    const emptyForm = () => ({
      identId: '',
      authCode: '',
      members: [emptyHeadMember()],
      protectionResidenceSame: '',
      pastAddresses: [],
      bank: {
        accountHolderName: '',
        bankName: '',
        branchName: '',
        accountType: '1',
        accountNumber: '',
      },
      otherHouseholdMember: emptyOtherHouseholdMember(),
      otherHouseholdMembers: [emptyOtherHouseholdMember()],
      additionRows: [],
      requiredDocs: emptyRequiredDocs(),
      consentForm: {
        date: '',
        y: '',
        m: '',
        d: '',
        address: '',
        name: '',
      },
      attachments: buildEmptyAttachments(),
    });

    function ApplicationFormFormat() {
      const [form, setForm] = useState(() => emptyForm());
      const [page, setPage] = useState('basic'); // 'basic' | 'auth' | 'notice' | 'form' | 'confirm' | 'sent'
      const [privacyAgreed, setPrivacyAgreed] = useState(false);
      const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState('');
      const [showValidation, setShowValidation] = useState(false);
      const [basicLocked, setBasicLocked] = useState(false);
      const [openPreview, setOpenPreview] = useState(''); // `${attachmentKey}:${partKey}`
      const [submitting, setSubmitting] = useState(false);
      const [toastItems, setToastItems] = useState([]);
      const toastIdRef = useRef(0);
      const [errorDialog, setErrorDialog] = useState('');
      const normalizedPrivacyPolicyUrl = String(privacyPolicyUrl || '').trim();
      const hasPrivacyPolicyUrl = normalizedPrivacyPolicyUrl !== '';

      const dismissToast = useCallback((id) => {
        setToastItems((prev) => prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)));
        setTimeout(() => {
          setToastItems((prev) => prev.filter((t) => t.id !== id));
        }, 300);
      }, []);

      const showToast = useCallback((message, type = 'success') => {
        const text = String(message || '');
        if (!text) return;
        const id = ++toastIdRef.current;
        setToastItems((prev) => {
          // Deduplicate: skip if same message already visible (not exiting)
          if (prev.some((t) => t.message === text && !t.exiting)) return prev;
          const next = [...prev, { id, message: text, type, exiting: false }];
          const active = next.filter((t) => !t.exiting);
          if (active.length > TOAST_MAX_VISIBLE) {
            const oldest = active[0];
            return next.map((t) => (t.id === oldest.id ? { ...t, exiting: true } : t));
          }
          return next;
        });
        if (type !== 'error') {
          setTimeout(() => {
            setToastItems((prev) => prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)));
            setTimeout(() => {
              setToastItems((prev) => prev.filter((t) => t.id !== id));
            }, 300);
          }, 3000);
        }
      }, []);

      const requiredLabel = (label) => (
        <span className="inline-flex items-center gap-1">
          <span aria-hidden className="text-red-500 text-sm font-bold">*</span>
          <span>{label}</span>
        </span>
      );

      const updateField = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));
      const updateBank = (key, val) =>
        setForm((prev) => ({
          ...prev,
          bank: {
            ...prev.bank,
            [key]: sanitizeBankFieldInput(key, val),
          },
        }));

      const updateMember = (idx, key, val) =>
        setForm((prev) => ({
          ...prev,
          members: prev.members.map((m, i) => (i === idx ? { ...m, [key]: key === 'phone' ? phoneDigitsOnly(val) : val } : m)),
        }));
      const updateMemberRelation = (idx, val) =>
        setForm((prev) => ({
          ...prev,
          members: prev.members.map((m, i) =>
            i === idx
              ? idx === 0
                ? m
                : { ...m, relationToHead: val, relationToHeadOther: val === 'その他' ? (m.relationToHeadOther || '') : '' }
              : m
          ),
        }));
      const addMember = () => setForm((prev) => ({ ...prev, members: [...prev.members, emptyMember()] }));
      const removeMember = (idx) =>
        setForm((prev) =>
          prev.members.length <= 1 || idx === 0 ? prev : { ...prev, members: prev.members.filter((_, i) => i !== idx) }
        );

      const updatePastField = (idx, field, val) =>
        setForm((prev) => ({
          ...prev,
          pastAddresses: prev.pastAddresses.map((p, i) => (i === idx ? { ...p, [field]: val } : p)),
        }));
      const addPastAddress = () =>
        setForm((prev) =>
          prev.pastAddresses.length >= 3 ? prev : { ...prev, pastAddresses: [...prev.pastAddresses, emptyPastAddress()] }
        );
      const removePastAddress = (idx) =>
        setForm((prev) => ({ ...prev, pastAddresses: prev.pastAddresses.filter((_, i) => i !== idx) }));

      const updateOther = (idx, key, val) =>
        setForm((prev) => {
          const rows = otherHouseholdContactsOf(prev);
          const nextRows = rows.map((row, i) => {
            if (i !== idx) return row;
            const nextRow = { ...emptyOtherHouseholdMember(), ...row, [key]: String(key).startsWith('phone') ? phoneDigitsOnly(val) : val };
            if (key === 'phone') {
              nextRow.phone1 = '';
              nextRow.phone2 = '';
              nextRow.phone3 = '';
            }
            return nextRow;
          });
          return { ...prev, otherHouseholdMembers: nextRows, otherHouseholdMember: nextRows[0] || emptyOtherHouseholdMember() };
        });
      const addOtherHouseholdMember = () =>
        setForm((prev) => {
          const rows = otherHouseholdContactsOf(prev);
          if (rows.length >= 2) return prev;
          const nextRows = [...rows, emptyOtherHouseholdMember()];
          return { ...prev, otherHouseholdMembers: nextRows, otherHouseholdMember: nextRows[0] || emptyOtherHouseholdMember() };
        });
      const removeOtherHouseholdMember = (idx) =>
        setForm((prev) => {
          const nextRows = otherHouseholdContactsOf(prev).filter((_, i) => i !== idx);
          const rows = nextRows.length ? nextRows : [emptyOtherHouseholdMember()];
          return { ...prev, otherHouseholdMembers: rows, otherHouseholdMember: rows[0] || emptyOtherHouseholdMember() };
        });

      const updateConsent = (patch) =>
        setForm((prev) => ({ ...prev, consentForm: { ...(prev.consentForm || {}), ...(patch || {}) } }));
      const updateConsentDate = (val) => {
        const [y = '', m = '', d = ''] = val ? String(val).split('-') : [];
        updateConsent({ date: val || '', y, m, d });
      };

      const updateAddition = (idx, key, val) =>
        setForm((prev) => ({
          ...prev,
          additionRows: prev.additionRows.map((a, i) => (i === idx ? { ...a, [key]: val } : a)),
        }));
      const addAdditionRow = () =>
        setForm((prev) => ({
          ...prev,
          additionRows: [...prev.additionRows, { category: APP_DOC_ADDON_TYPES[0], content: '', upload: { name: '', previewUrl: '' }, uploads: [] }],
        }));
      const removeAdditionRow = (idx) =>
        setForm((prev) => ({ ...prev, additionRows: prev.additionRows.filter((_, i) => i !== idx) }));

      const updateRequiredDoc = (key, val) =>
        setForm((prev) => ({ ...prev, requiredDocs: { ...(prev.requiredDocs || {}), [key]: !!val } }));

      const getAttPart = (atts, k, partKey) => (atts && atts[k] && atts[k][partKey]) ? atts[k][partKey] : attachmentEmptyPart();
      const setAttachmentPart = (key, partKey, part) => {
        setForm((prev) => ({
          ...prev,
          attachments: {
            ...(prev.attachments || {}),
            [key]: {
              ...((prev.attachments || {})[key] || {}),
              [partKey]: { ...attachmentEmptyPart(), ...(part || {}) },
            },
          },
        }));
      };

      const SINGLE_FILE_PART_ATTACHMENT_KEYS = new Set(['mynumber', 'license', 'residenceCard', 'disabilityBook']);
      const isSingleFilePartAttachment = (key, partKey) =>
        SINGLE_FILE_PART_ATTACHMENT_KEYS.has(key) && (partKey === 'front' || partKey === 'back');

      const setAttachmentImagesFromFiles = async (key, partKey, files) => {
        const list = Array.from(files || []);
        if (list.length === 0) return;
        const max = isSingleFilePartAttachment(key, partKey) ? 1 : MAX_FILES_PER_ATTACHMENT;

        // 既存の images を取得（追記式にする）
        const existing = (form.attachments?.[key]?.[partKey]?.images) || [];
        const remainingSlots = Math.max(0, max - existing.length);
        if (remainingSlots <= 0) {
          showToast(`ファイルは最大${max}件までです。`, 'warning');
          return;
        }

        const accepted = [];
        let rejectedByValidation = 0;
        for (const f of list) {
          if (accepted.length >= remainingSlots) break;
          const err = validateAttachmentFile(f);
          if (err) {
            showToast(err, 'warning');
            rejectedByValidation++;
            continue;
          }
          accepted.push(f);
        }
        if (list.length - rejectedByValidation > remainingSlots) {
          showToast(`ファイルは最大${max}件までです。${remainingSlots}件に制限して取り込みました。`, 'warning');
        }
        if (accepted.length === 0) return;

        const readOne = (f) =>
          new Promise((resolve) => {
            if (isPdfFile(f.name)) {
              resolve({
                fileName: f.name,
                previewUrl: typeof URL !== 'undefined' && URL.createObjectURL ? URL.createObjectURL(f) : '',
                file: f,
                isPdf: true,
              });
              return;
            }
            const reader = new FileReader();
            reader.onload = () => resolve({
              fileName: f.name,
              previewUrl: typeof reader.result === 'string' ? reader.result : '',
              file: f,
              isPdf: false,
            });
            reader.readAsDataURL(f);
          });
        const newImages = await Promise.all(accepted.map(readOne));
        setAttachmentPart(key, partKey, { images: [...existing, ...newImages] });
      };

      const removeAttachmentImage = (key, partKey, idx) => {
        setForm((prev) => {
          const part = (prev.attachments && prev.attachments[key] && prev.attachments[key][partKey]) || attachmentEmptyPart();
          const images = Array.isArray(part.images) ? part.images.filter((_, i) => i !== idx) : [];
          return {
            ...prev,
            attachments: {
              ...(prev.attachments || {}),
              [key]: {
                ...((prev.attachments || {})[key] || {}),
                [partKey]: { ...attachmentEmptyPart(), images },
              },
            },
          };
        });
      };

      const setAdditionUploadsFromFiles = async (idx, files) => {
        const list = Array.from(files || []);
        if (list.length === 0) return;

        const max = MAX_FILES_PER_ATTACHMENT;
        const existing = (form.additionRows?.[idx]?.uploads) || [];
        const remainingSlots = Math.max(0, max - existing.length);
        if (remainingSlots <= 0) {
          showToast(`ファイルは最大${max}件までです。`, 'warning');
          return;
        }

        const accepted = [];
        let rejectedByValidation = 0;
        for (const f of list) {
          if (accepted.length >= remainingSlots) break;
          const err = validateAttachmentFile(f);
          if (err) {
            showToast(err, 'warning');
            rejectedByValidation++;
            continue;
          }
          accepted.push(f);
        }
        if (list.length - rejectedByValidation > remainingSlots) {
          showToast(`ファイルは最大${max}件までです。${remainingSlots}件に制限して取り込みました。`, 'warning');
        }
        if (accepted.length === 0) return;

        const readOne = (f) =>
          new Promise((resolve) => {
            if (isPdfFile(f.name)) {
              resolve({
                name: f.name,
                previewUrl: typeof URL !== 'undefined' && URL.createObjectURL ? URL.createObjectURL(f) : '',
                file: f,
                isPdf: true,
              });
              return;
            }
            const reader = new FileReader();
            reader.onload = () => resolve({
              name: f.name,
              previewUrl: typeof reader.result === 'string' ? reader.result : '',
              file: f,
              isPdf: false,
            });
            reader.readAsDataURL(f);
          });
        const newUploads = await Promise.all(accepted.map(readOne));
        setForm((prev) => ({
          ...prev,
          additionRows: prev.additionRows.map((a, i) => {
            if (i !== idx) return a;
            const existingUploads = Array.isArray(a.uploads) ? a.uploads : [];
            const uploads = [...existingUploads, ...newUploads];
            return { ...a, upload: uploads[0] ?? a.upload, uploads };
          }),
        }));
      };

      const removeAdditionUpload = (idx, fileIdx) => {
        setForm((prev) => ({
          ...prev,
          additionRows: prev.additionRows.map((a, i) => {
            if (i !== idx) return a;
            const existingUploads = Array.isArray(a.uploads) ? a.uploads : (a.upload?.file ? [a.upload] : []);
            const uploads = existingUploads.filter((_, j) => j !== fileIdx);
            const upload = uploads[0] || { name: '', previewUrl: '' };
            return { ...a, upload, uploads };
          }),
        }));
      };

      const cloudFrontBlockMessage =
        'サーバー手前のCloudFrontで送信がブロックされました。添付ファイルのサイズを減らして再度お試しください。解決しない場合は管理者に連絡してください。';

      const responseMessage = async (response, fallback) => {
        const text = await response.text();
        if (!text) return fallback;
        try {
          const body = JSON.parse(text);
          return body.message || body.error || fallback;
        } catch (_) {
          const contentType = response.headers.get('content-type') || '';
          const trimmed = text.trim();
          const looksLikeHtml = contentType.includes('text/html')
            || /^<!doctype html/i.test(trimmed)
            || /^<html/i.test(trimmed);
          if (looksLikeHtml) {
            return response.status === 403 || /cloudfront|request blocked/i.test(trimmed)
              ? cloudFrontBlockMessage
              : fallback;
          }
          return text;
        }
      };

      const buildSubmitPayload = () => {
        const attachmentSummary = Object.fromEntries(
          APP_DOC_ATTACHMENT_DEFS.map((def) => {
            const parts = form.attachments?.[def.key] || {};
            return [
              def.key,
              Object.fromEntries(Object.entries(parts).map(([partKey, part]) => [
                partKey,
                {
                  images: Array.isArray(part?.images)
                    ? part.images.map((img) => ({ fileName: img.fileName || '' }))
                    : [],
                },
              ])),
            ];
          })
        );
        const otherHouseholdMembers = otherHouseholdContactsOf(form)
          .filter(hasOtherHouseholdContactValue)
          .map(normalizeOtherHouseholdContactForSubmit);
        return {
          identId: form.identId,
          authCode: form.authCode,
          applicantPhone: phoneDigitsOnly(form.members?.[0]?.phone),
          members: (form.members || []).map(normalizeMemberForSubmit),
          protectionResidenceSame: form.protectionResidenceSame,
          pastAddresses: (form.pastAddresses || []).map(normalizePastAddressForSubmit),
          bank: form.bank || {},
          otherHouseholdMember: otherHouseholdMembers[0] || {},
          otherHouseholdMembers,
          additionRows: (form.additionRows || []).map((row) => ({
            category: row.category || '',
            content: row.content || '',
            uploadName: row.uploads?.[0]?.name || row.upload?.name || '',
          })),
          requiredDocs: form.requiredDocs || {},
          consentForm: normalizeConsentForSubmit(form.consentForm || {}),
          privacyAgreed,
          attachments: attachmentSummary,
        };
      };

      const buildSubmitFormData = () => {
        const data = new FormData();
        data.append('payload', JSON.stringify(buildSubmitPayload()));
        APP_DOC_ATTACHMENT_DEFS.forEach((def) => {
          const parts = form.attachments?.[def.key] || {};
          Object.entries(parts).forEach(([partKey, part]) => {
            (part?.images || []).forEach((img) => {
              if (img.file) {
                data.append(`attachments.${def.key}.${partKey}`, img.file, img.fileName || img.file.name);
              }
            });
          });
        });
        (form.additionRows || []).forEach((row, idx) => {
          const uploads = Array.isArray(row.uploads) && row.uploads.length
            ? row.uploads
            : (row.upload?.file ? [row.upload] : []);
          uploads.forEach((u) => {
            if (u && u.file) {
              data.append(`additionEvidence.${idx}`, u.file, u.name || u.file.name);
            }
          });
        });
        return data;
      };

      const computeErrors = () => {
        const errs = {};

        const members = Array.isArray(form.members) ? form.members : [];
        const hasMemberName = members.some((m) => m && String(m.name || '').trim() !== '');
        if (!hasMemberName) errs.members = true;

        const memberRow = members.map((m, mi) => {
          const e = {};
          const fg = String(m?.furigana || '').trim();
          if (!fg) e.furigana = 'required';
          else if (!isValidFurigana(fg)) e.furigana = 'invalid';
          if (String(m?.name || '').trim() === '') e.name = 'required';
          if (String(m?.relationToHead || '').trim() === '') e.relationToHead = 'required';
          else if (m.relationToHead === 'その他' && String(m?.relationToHeadOther || '').trim() === '')
            e.relationToHeadOther = 'required';
          if (String(m?.birthDate || '').trim() === '') e.birthDate = 'required';
          const phone = phoneDigitsOnly(m?.phone);
          if (mi === 0 && !phone) e.phone = 'required';
          else if (phone && !isValidPhoneNumber(phone)) e.phone = 'invalid';
          const startEmpty = !isIsoDate(m?.benefitPeriodStartMonth);
          const endEmpty = !isIsoDate(m?.benefitPeriodEndMonth);
          if (startEmpty) e.benefitPeriodStart = 'required';
          if (endEmpty) e.benefitPeriodEnd = 'required';
          if (!startEmpty && !endEmpty && m.benefitPeriodStartMonth > m.benefitPeriodEndMonth) {
            e.benefitPeriodEnd = 'order';
          }
          const email = String(m?.email || '').trim();
          if (mi === 0 && !email) e.email = 'required';
          else if (email && !isValidEmail(email)) e.email = 'invalid';
          return e;
        });
        if (memberRow.some((e) => Object.keys(e).length > 0)) {
          errs.memberRow = memberRow;
        }

        if (form.protectionResidenceSame !== 'same' && form.protectionResidenceSame !== 'different') errs.residenceRadio = true;
        if (form.protectionResidenceSame === 'different') {
          const rows = Array.isArray(form.pastAddresses) ? form.pastAddresses : [];
          const anyValid = rows.some((p) => String(p?.address || '').trim() !== '' && validMonthRange(p?.periodStartMonth, p?.periodEndMonth));
          if (!anyValid) errs.pastAddresses = true;
          errs.pastAddressRow = rows.map((p) => ({
            address: String(p?.address || '').trim() === '',
            period: !validMonthRange(p?.periodStartMonth, p?.periodEndMonth),
          }));
        }

        const b = form.bank || {};
        const bankErrs = {};
        const bankNameError = bankCodeError(b.bankName);
        if (bankNameError) bankErrs.bankName = bankNameError;
        const branchError = bankBranchNumberError(b.branchName);
        if (branchError) bankErrs.branchName = branchError;
        if (String(b.accountType || '').trim() === '') bankErrs.accountType = 'required';
        const accountNumberError = bankAccountNumberError(b.accountNumber);
        if (accountNumberError) bankErrs.accountNumber = accountNumberError;
        const accountHolderError = bankAccountHolderError(b.accountHolderName);
        if (accountHolderError) bankErrs.accountHolderName = accountHolderError;
        if (Object.keys(bankErrs).length > 0) errs.bank = bankErrs;

        const otherContactRows = otherHouseholdContactsOf(form);
        const otherContactErrRows = otherContactRows.map((ohm) => {
          const ohmErrs = {};
          const phone = otherHouseholdPhoneDigits(ohm);
          if (phone) {
            if (!isValidPhoneNumber(phone)) ohmErrs.phone = 'invalid';
          }
          const ohmEmail = String(ohm.email || '').trim();
          if (ohmEmail && !isValidEmail(ohmEmail)) ohmErrs.email = 'invalid';
          return ohmErrs;
        });
        if (otherContactErrRows.some((row) => Object.keys(row).length > 0)) {
          errs.otherHouseholdMembers = otherContactErrRows;
          errs.otherHouseholdMember = otherContactErrRows[0] || {};
        }

        // 添付（証明書等）：必須提出書類に対応する添付があるか（チェック状態に関係なく必須）
        const atts = form.attachments || {};
        const hasAnyAttachment = (k) => {
          const parts = atts[k] || {};
          return Object.values(parts).some((p) => Array.isArray((p || {}).images) && (p || {}).images.length > 0);
        };
        const idKeys = ['mynumber', 'license', 'residenceCard', 'disabilityBook', 'passport'];
        const missingKeys = new Set();

        // 本人確認書類：いずれか1点必須
        const hasAnyId = idKeys.some((k) => hasAnyAttachment(k));
        if (!hasAnyId) idKeys.forEach((k) => missingKeys.add(k));

        // 戸籍謄本（全部事項証明書）：必須
        if (!hasAnyAttachment('kosekiAll')) missingKeys.add('kosekiAll');

        // 口座確認（通帳/キャッシュカード）：必須
        if (!hasAnyAttachment('bankBookOrCard')) missingKeys.add('bankBookOrCard');

        if (missingKeys.size > 0) {
          errs.attachments = true;
          errs.missingAttachmentKeys = Array.from(missingKeys);
        }

        const cf = form.consentForm || {};
        const consentMissing =
          String(consentDateValue(cf) || '').trim() === '' ||
          String(cf.address || '').trim() === '' ||
          String(cf.name || '').trim() === '';
        if (consentMissing) errs.consentForm = true;

        if (!hasPrivacyPolicyUrl || !privacyAgreed) errs.privacy = true;

        const rd = form.requiredDocs || {};
        if (CHECKLIST_REQUIRED_KEYS.some((k) => !rd[k])) errs.checklist = true;

        return errs;
      };

      const goConfirm = () => {
        setShowValidation(true);
        const errs = computeErrors();
        const hasErrors =
          Object.keys(errs).some((k) => k !== 'pastAddressRow') ||
          (Array.isArray(errs.pastAddressRow) && errs.pastAddressRow.some((r) => r.address || r.period));
        if (hasErrors) {
          window.scrollTo(0, 0);
          return;
        }
        setPage('confirm');
        window.scrollTo(0, 0);
      };
      const goForm = () => setPage('form');
      const goFormFromNotice = () => {
        setShowValidation(false);
        setPage('form');
        window.scrollTo(0, 0);
      };
      const submit = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
          const response = await fetch('/api/public/application-documents/submit', {
            method: 'POST',
            body: buildSubmitFormData(),
          });
          if (!response.ok) {
            throw new Error(await responseMessage(response, '送信に失敗しました。'));
          }
          setPage('sent');
          window.scrollTo(0, 0);
        } catch (error) {
          setErrorDialog(error?.message || '送信に失敗しました。');
        } finally {
          setSubmitting(false);
        }
      };

      const goMainFromBasic = () => {
        setShowValidation(true);
        const idOk = String(form.identId || '').trim() !== '';
        if (!idOk) {
          window.scrollTo(0, 0);
          return;
        }
        setShowValidation(false);
        setPrivacyAgreed(false);
        setPrivacyPolicyUrl('');
        setPage('auth');
        window.scrollTo(0, 0);
      };

      const goAuthBack = () => {
        setShowValidation(false);
        setPrivacyAgreed(false);
        setPrivacyPolicyUrl('');
        setForm((prev) => ({ ...prev, authCode: '' }));
        setPage('basic');
        window.scrollTo(0, 0);
      };

      const goMainFromAuth = async () => {
        setShowValidation(true);
        const codeOk = String(form.authCode || '').trim() !== '';
        if (!codeOk) {
          window.scrollTo(0, 0);
          return;
        }
        if (submitting) return;
        setSubmitting(true);
        try {
          const response = await fetch('/api/public/application-documents/verify', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ identId: form.identId, authCode: form.authCode }),
          });
          if (!response.ok) {
            throw new Error(await responseMessage(response, '認証情報の確認に失敗しました。'));
          }
          const verified = await response.json().catch(() => ({}));
          if (!isApplicationEntryAllowedStatus(verified?.caseStatus)) {
            throw new Error(APPLICATION_ENTRY_NOT_ALLOWED_MESSAGE);
          }
          setPrivacyPolicyUrl(String(verified?.privacyPolicyUrl || '').trim());
          setPrivacyAgreed(false);
          setBasicLocked(true);
          setShowValidation(false);
          setPage('notice');
          window.scrollTo(0, 0);
        } catch (error) {
          setErrorDialog(error?.message || '認証情報の確認に失敗しました。');
        } finally {
          setSubmitting(false);
        }
      };

      const memberTextFields = [
        ['furigana', 'フリガナ', 'text', undefined],
        ['name', '氏名', 'text', undefined],
        ['phone', '電話番号', 'tel', 'tel'],
        ['email', 'メールアドレス', 'email', 'email'],
      ];

      const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent';
      const labelCls = 'block text-sm font-medium text-gray-700 mb-1';
      const errorBoxCls = 'border-2 border-red-500 ring-2 ring-red-200';

      const displayText = (v, fallback = '—') => {
        const s = v == null ? '' : String(v);
        return s.trim() === '' ? fallback : s;
      };

      const attachmentSummary = (part) => {
        const p = part || attachmentEmptyPart();
        const n = Array.isArray(p.images) ? p.images.length : 0;
        return n > 0 ? `ファイル：${n}件` : '未添付';
      };

      const uploadFileName = (file) => file?.fileName || file?.name || '(無名)';
      const uploadPreviewSrc = (file) => {
        if (file?.previewUrl) return file.previewUrl;
        if (file?.file && typeof URL !== 'undefined' && URL.createObjectURL) {
          return URL.createObjectURL(file.file);
        }
        return '';
      };

      const ConfirmFilePreviewList = ({ files, previewIdPrefix }) => {
        const list = Array.isArray(files) ? files.filter(Boolean) : [];
        if (list.length === 0) {
          return <div className="text-sm text-gray-500">添付なし</div>;
        }
        return (
          <div className="space-y-2">
            <div className="text-xs font-medium text-gray-500">アップロード済みファイル一覧</div>
            {list.map((file, idx) => {
              const name = uploadFileName(file);
              const previewId = `${previewIdPrefix}:${idx}`;
              const isOpen = openPreview === previewId;
              const isPdf = file?.isPdf || isPdfFile(name);
              const src = isOpen ? uploadPreviewSrc(file) : '';
              return (
                <div key={`${name}-${idx}`} className="rounded-lg border border-gray-200 bg-white p-3">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 break-all">{name}</div>
                      <div className="text-xs text-gray-500">{isPdf ? 'PDF' : '画像'}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setOpenPreview((prev) => (prev === previewId ? '' : previewId))}
                      className="no-print self-start sm:self-center px-3 py-1.5 text-xs rounded-lg border border-gray-300 hover:bg-gray-50 transition cursor-pointer"
                    >
                      {isOpen ? '非表示' : 'プレビュー'}
                    </button>
                  </div>
                  {isOpen ? (
                    <div className="mt-3">
                      {src ? (
                        isPdf ? (
                          <iframe title={`プレビュー: ${name}`} src={src} className="w-full h-80 rounded border border-gray-200 bg-gray-50" />
                        ) : (
                          <img src={src} alt={`プレビュー: ${name}`} className="w-full max-h-96 object-contain rounded border border-gray-200 bg-gray-50" />
                        )
                      ) : (
                        <div className="rounded border border-gray-200 bg-gray-50 px-3 py-6 text-center text-sm text-gray-500">プレビューを表示できません。</div>
                      )}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        );
      };

      const ConfirmRow = ({ label, value }) => (
        <div className="flex flex-col sm:flex-row sm:justify-between gap-1 py-2 border-b border-gray-100">
          <span className="text-gray-500 shrink-0">{label}</span>
          <span className="font-medium text-gray-900 break-all">{displayText(value)}</span>
        </div>
      );

      const basicBody = (
        <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
          <div className="bg-white rounded-xl shadow-lg p-6 print-break">
            <h3 className="text-lg font-bold text-gray-800 mb-2">基本情報</h3>
            <p className="text-sm text-gray-600 mb-4">識別IDを入力してください。</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>{requiredLabel('識別ID')}</label>
                <input
                  type="text"
                  value={form.identId}
                  onChange={(e) => updateField('identId', e.target.value)}
                  className={`${inputCls} ${showValidation && String(form.identId || '').trim() === '' ? errorBoxCls : ''}`}
                />
                {showValidation && String(form.identId || '').trim() === '' ? (
                  <div className="customer-inline-error">識別IDは必須です。</div>
                ) : null}
              </div>
            </div>
            <div className="flex justify-end pt-6">
              <button
                type="button"
                onClick={goMainFromBasic}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-md cursor-pointer"
              >
                次へ
              </button>
            </div>
          </div>
        </div>
      );

      const authBody = (
        <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
          <div className="bg-white rounded-xl shadow-lg p-6 print-break">
            <h3 className="text-lg font-bold text-gray-800 mb-2">認証</h3>
            <p className="text-sm text-gray-600 mb-4">認証コードを入力してください。</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>{requiredLabel('認証コード')}</label>
                <input
                  type="text"
                  value={form.authCode}
                  onChange={(e) => updateField('authCode', e.target.value)}
                  className={`${inputCls} ${showValidation && String(form.authCode || '').trim() === '' ? errorBoxCls : ''}`}
                  autoComplete="one-time-code"
                />
                {showValidation && String(form.authCode || '').trim() === '' ? (
                  <div className="customer-inline-error">認証コードは必須です。</div>
                ) : null}
              </div>
            </div>
            <div className="flex flex-wrap justify-end gap-3 pt-6">
              <button
                type="button"
                onClick={goAuthBack}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition cursor-pointer"
              >
                戻る
              </button>
              <button
                type="button"
                onClick={goMainFromAuth}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-md cursor-pointer"
              >
                次へ
              </button>
            </div>
          </div>
        </div>
      );

      const noticeBody = (
        <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
          <div className="bg-white rounded-xl shadow-lg p-6 print-break">
            <h2 className="text-xl font-bold text-gray-800 mb-2">入力を始める前に</h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              入力内容が不足すると最初からやり直しになる場合があります。スムーズにご入力いただくためにも、必ず事前に以下の情報をご確認いただき、必要情報をご用意のうえ入力を開始してください。
            </p>
          </div>

          <div className="space-y-6">
            {FORM_ENTRY_NOTICE_GROUPS.map((group) => (
              <section key={group.title} className="bg-white rounded-xl shadow-lg p-6 print-break">
                <h2 className="text-xl font-bold text-gray-800 mb-2">{group.title}</h2>
                {group.items.length === 1 ? (
                  <p className="text-sm text-gray-700 leading-relaxed">{group.items[0]}</p>
                ) : (
                  <ul className="space-y-2 text-sm text-gray-800 leading-relaxed">
                    {group.items.map((item) => (
                      <li key={item} className="flex gap-2">
                        <span className="mt-[0.55em] h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" aria-hidden="true" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 print-break">
            <h3 className="text-base font-bold text-gray-800 mb-3">入力時の注意</h3>
            <div className="space-y-2 text-sm text-gray-800 leading-relaxed">
              <p>口座名義は半角カタカナで入力してください。振込先は申出者本人名義の口座に限ります。</p>
              <p>添付できるファイルは PDF / JPG / JPEG / PNG、1ファイルあたり 5MB 以内です。</p>
              <p>必須項目や必須添付書類が不足している場合は、入力確認画面へ進めません。</p>
            </div>
          </div>

          <div className="flex flex-wrap justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                setShowValidation(false);
                setPage('auth');
                window.scrollTo(0, 0);
              }}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition cursor-pointer"
            >
              戻る
            </button>
            <button
              type="button"
              onClick={goFormFromNotice}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-md cursor-pointer"
            >
              入力を始める
            </button>
          </div>
        </div>
      );

      const confirmBody = (
        <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-1">入力確認</h2>
            <p className="text-sm text-gray-600">入力内容を確認し、問題なければ送信してください。</p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 print-break">
            <h3 className="text-lg font-bold text-gray-800 mb-4">基本情報</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <ConfirmRow label="識別ID" value={form.identId} />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 print-break">
            <h3 className="text-lg font-bold text-gray-800 mb-4">1. 申出者等（世帯員）</h3>
            <div className="space-y-4">
              {form.members.map((m, i) => (
                <div key={i} className="border border-gray-200 rounded-lg p-4 bg-gray-50/80">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-gray-700">世帯員 {i + 1}</span>
                    {m.death === 'maru' ? <span className="text-xs font-medium text-red-600">死亡</span> : null}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 text-sm">
                    <ConfirmRow label="フリガナ" value={m.furigana} />
                    <ConfirmRow label="氏名" value={m.name} />
                    <ConfirmRow label="世帯主との続柄" value={normalizeRelationToHead(m)} />
                    <ConfirmRow label="生年月日" value={m.birthDate} />
                    <ConfirmRow label="電話番号" value={phoneDigitsOnly(m.phone)} />
                    <ConfirmRow label="メールアドレス" value={m.email} />
                    <ConfirmRow label="生活保護受給期間" value={dateRangeLabel(m.benefitPeriodStartMonth, m.benefitPeriodEndMonth, m.benefitPeriod)} />
                  </div>
                </div>
              ))}
            </div>
            {MEMBER_FOOTNOTES}
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 print-break">
            <h3 className="text-lg font-bold text-gray-800 mb-3 leading-snug">2. 保護を受給していた当時の居所</h3>
            <ConfirmRow label="現住所との関係" value={form.protectionResidenceSame === 'different' ? '現住所と異なる' : '現住所と同じ'} />
            {form.protectionResidenceSame === 'different' ? (
              <div className="mt-4 space-y-3">
                {(form.pastAddresses || []).map((p, i) => (
                  <div key={i} className="border border-gray-200 rounded-lg p-3">
                    <div className="text-xs text-gray-500 mb-1">#{i + 1}</div>
                    <ConfirmRow label="当時の居所" value={p.address} />
                    <ConfirmRow label="期間" value={monthRangeLabel(p.periodStartMonth, p.periodEndMonth, p.period)} />
                  </div>
                ))}
              </div>
            ) : null}
            {RESIDENCE_FOOTNOTES}
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 print-break">
            <h3 className="text-lg font-bold text-gray-800 mb-3">3. 口座情報</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <ConfirmRow label="銀行番号" value={form.bank.bankName} />
              <ConfirmRow label="支店番号" value={form.bank.branchName} />
              <ConfirmRow label="預金種目" value={bankAccountTypeLabel(form.bank.accountType)} />
              <ConfirmRow label="口座番号" value={form.bank.accountNumber} />
              <ConfirmRow label="口座名義" value={form.bank.accountHolderName} />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 print-break">
            <h3 className="text-lg font-bold text-gray-800 mb-3">4. 申出者以外の世帯員の連絡先</h3>
            {(() => {
              const rows = otherHouseholdContactsOf(form).filter(hasOtherHouseholdContactValue);
              return rows.length === 0 ? (
                <p className="text-sm text-gray-500">入力なし</p>
              ) : (
                <div className="space-y-3 text-sm">
                  {rows.map((row, i) => (
                    <div key={i} className="border border-gray-200 rounded-lg p-3">
                      <div className="text-xs text-gray-500 mb-1">#{i + 1}</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <ConfirmRow label="他の世帯員の氏名" value={row.name} />
                        <ConfirmRow label="電話番号" value={otherHouseholdPhoneDigits(row)} />
                        <ConfirmRow label="メールアドレス" value={row.email} />
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 print-break">
            <h3 className="text-lg font-bold text-gray-800 mb-3">5. 加算等の申出</h3>
            <div className="space-y-4">
              {(form.additionRows || []).length === 0 ? (
                <p className="text-sm text-gray-500">入力なし</p>
              ) : (
                form.additionRows.map((a, i) => (
                  <div key={i} className="border border-gray-200 rounded-lg p-4">
                    <div className="text-xs text-gray-500 mb-1">#{i + 1}</div>
                    <ConfirmRow label="種類" value={a.category} />
                    <ConfirmRow label="該当する内容" value={a.content} />
                    <ConfirmRow label="挙証資料の例" value={evidenceExampleByCategory(a.category)} />
                    <div className="mt-3">
                      <div className="text-sm font-medium text-gray-700 mb-1">添付（ファイル）</div>
                      {(() => {
                        const uploads = Array.isArray(a.uploads) && a.uploads.length
                          ? a.uploads
                          : (a.upload && (a.upload.previewUrl || a.upload.name) ? [a.upload] : []);
                        if (uploads.length === 0) {
                          return <div className="text-sm text-gray-500">添付なし</div>;
                        }
                        return <ConfirmFilePreviewList files={uploads} previewIdPrefix={`confirm-addition-${i}`} />;
                      })()}
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="mt-6">
              {CLOSING_NOTES}
            </div>
          </div>

          {RYUI_JIKO}

          <div className="bg-white rounded-xl shadow-lg p-6 print-break">
              <h3 className="text-lg font-bold text-gray-800 mb-3">添付書類（証明書等）</h3>
            <div className="space-y-4">
              {APP_DOC_ATTACHMENT_DEFS.map((def) => {
                const parts = (def.parts && def.parts.length ? def.parts : [{ key: 'single', label: '' }]);
                return (
                  <div key={def.key} className="border border-gray-200 rounded-lg p-3 bg-gray-50/50">
                    <div className="text-sm font-medium text-gray-800 mb-2 leading-snug whitespace-pre-line">{def.label}</div>
                      <div className="space-y-2">
                        {parts.map((p) => {
                          const part = getAttPart(form.attachments, def.key, p.key);
                          const images = Array.isArray(part.images) ? part.images : [];
                          return (
                            <div key={p.key} className="border border-gray-200 rounded-lg bg-white p-3">
                              {p.label ? <div className="text-sm font-medium text-gray-700 mb-1">{p.label}</div> : null}
                              <div className="text-sm text-gray-900 mb-2">{attachmentSummary(part)}</div>
                              {images.length > 0 ? (
                                <ConfirmFilePreviewList files={images} previewIdPrefix={`confirm-attachment-${def.key}-${p.key}`} />
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 print-break">
            <h3 className="text-lg font-bold text-gray-800 mb-4 text-center">同意書</h3>
            {showValidation && computeErrors().consentForm ? (
              <div className="customer-inline-error mb-3">同意書の記入（年月日・住所・氏名）は必須です。</div>
            ) : null}
            <div className={`text-sm text-gray-900 leading-relaxed space-y-4 ${showValidation && computeErrors().consentForm ? errorBoxCls + ' rounded-lg p-4' : ''}`}>
              <p className="text-right text-gray-700">福祉事務所長　殿</p>
              <p className="whitespace-pre-line">
                {`平成 25 年生活扶助基準改定に関する最高裁判決を踏まえた保護費の追加給付の決定のために必要があるときは、私及び私の世帯員（以下「私等」という。）の保護の受給状況を確認するため、貴福祉事務所が官公署、本人口座機関等（以下「官公署等」という。）に対し、必要な書類の閲覧若しくは資料の提供を求めることに同意します。\nまた、貴福祉事務所の調査又は報告要求に対し、官公署等が報告することについて、私等が同意している旨を官公署等に伝えて構いません。`}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-4">
                <div>
                  <label className={labelCls}>年月日</label>
                  <input
                    type="date"
                    value={consentDateValue(form.consentForm)}
                    onChange={(e) => updateConsentDate(e.target.value)}
                    className={inputCls}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
                <div><label className={labelCls}>住所</label><input type="text" value={form.consentForm?.address ?? ''} onChange={(e) => updateField('consentForm', { ...(form.consentForm || {}), address: e.target.value })} className={inputCls} /></div>
                <div><label className={labelCls}>氏名</label><input type="text" value={form.consentForm?.name ?? ''} onChange={(e) => updateField('consentForm', { ...(form.consentForm || {}), name: e.target.value })} className={inputCls} /></div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 print-break">
            <h3 className="text-lg font-bold text-gray-800 mb-4">申出に当たってのチェックリスト</h3>
            <div className="text-sm text-gray-900 leading-relaxed space-y-6">
              <div>
                <p className="font-bold mb-2">① 申出書に添付する書類</p>
                <p className="text-sm text-gray-800 mb-3">下記が全て添付されているかチェックの上、申出をお願いいたします。（電子申出（マイナポータル等）の場合は写真を添付、郵送の場合は写しを添付願います。）</p>
                <div className="border border-gray-300 rounded-lg p-4">
                  <p className="font-semibold mb-2">【必ずご提出いただく資料】</p>
                  <div className="space-y-2">
                    <label className="flex gap-2 items-start">
                      <input type="checkbox" className="mt-1" disabled checked={!!form.requiredDocs?.idPhoto} />
                      <span>以下のいずれか一つ（申出者の顔写真付きの本人確認書類）：マイナンバーカードの写し（番号の記載がない両面）、運転免許証の写し、在留カードの写し、障害者手帳の写し、パスポートの写し等</span>
                    </label>
                    <label className="flex gap-2 items-start">
                      <input type="checkbox" className="mt-1" disabled checked={!!form.requiredDocs?.kosekiAll} />
                      <span>全世帯員の戸籍謄本の写し（全部事項証明書）</span>
                    </label>
                    <div className="pl-6 text-sm text-gray-800 space-y-1">
                      <p>※ 当時の姓が現在と異なる場合は、併せて当時の姓が記載された戸籍謄本を添付してください。</p>
                      <p>※ 離婚等により、当時の世帯員が申出者の戸籍から除籍となっている場合、申出者において戸籍の第三者請求により、除籍となった世帯員の現在の戸籍謄本を請求することができます。</p>
                      <p>※ 保護廃止中の場合は、戸籍謄本に代えて保護受給証明書によることも可</p>
                    </div>
                    <label className="flex gap-2 items-start">
                      <input type="checkbox" className="mt-1" disabled checked={!!form.requiredDocs?.juminhyoForeign} />
                      <span>全世帯員の住民票の写し（外国人の場合）（※保護受給中の場合は住民票に代えて保護受給証明書によることも可）</span>
                    </label>
                    <label className="flex gap-2 items-start">
                      <input type="checkbox" className="mt-1" disabled checked={!!form.requiredDocs?.bankProof} />
                      <span>申出者本人の「3. 口座情報」に記載の口座情報が確認できる通帳の写し、またはキャッシュカードの写し</span>
                    </label>
                  </div>
                  <div className="mt-4">
                    <p className="font-semibold mb-2">【必要に応じて提出いただく資料】</p>
                    <div className="space-y-2">
                      <label className="flex gap-2 items-start">
                        <input type="checkbox" className="mt-1" disabled checked={!!form.requiredDocs?.addonEvidence} />
                        <span>「5. 加算等の申出」で必要とされる挙証資料</span>
                      </label>
                      <label className="flex gap-2 items-start">
                        <input type="checkbox" className="mt-1" disabled checked={!!form.requiredDocs?.kosekiFupyo} />
                        <span>当時の居所を記載できない場合は、戸籍謄本の附票の写し</span>
                      </label>
                      <label className="flex gap-2 items-start">
                        <input type="checkbox" className="mt-1" disabled checked={!!form.requiredDocs?.proxyDocs} />
                        <span>代理による申出を行う場合は、委任状、身分確認書類、本人との関係を証する書類</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <p className="font-bold mb-2">② 記載事項</p>
                <p className="text-sm text-gray-800 mb-3">下記が全て添付されているかチェックの上、申出をお願いいたします。（電子申出（マイナポータル等）の場合は写真を添付、郵送の場合は写しを添付願います。）</p>
                <div className="border border-gray-300 rounded-lg p-4 space-y-2">
                  <label className="flex gap-2 items-start">
                    <input type="checkbox" className="mt-1" disabled checked={!!form.requiredDocs?.headAtTime} />
                    <span>申出者が保護受給当時の世帯主（当時の世帯主が死亡している場合は、当時の世帯主に準ずる者）となっているか</span>
                  </label>
                  <label className="flex gap-2 items-start">
                    <input type="checkbox" className="mt-1" disabled checked={!!form.requiredDocs?.membersListed} />
                    <span>申出先の自治体で保護を受給されていた期間の世帯員が記載されているか（当該期間に世帯内にいない方は追加給付の対象外となります）</span>
                  </label>
                  <label className="flex gap-2 items-start">
                    <input type="checkbox" className="mt-1" disabled checked={!!form.requiredDocs?.bankNameMatch} />
                    <span>振込口座が申出者本人の名義となっているか</span>
                  </label>
                  <label className="flex gap-2 items-start">
                    <input type="checkbox" className="mt-1" disabled checked={!!form.requiredDocs?.additionDetails} />
                    <span>加算等の該当がある場合、該当する方の氏名や該当期間が記載されているか</span>
                  </label>
                  <label className="flex gap-2 items-start">
                    <input type="checkbox" className="mt-1" disabled checked={!!form.requiredDocs?.contactListed} />
                    <span>連絡先が記載されているか</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 justify-end pb-10 items-start no-print">
            <div className="flex flex-wrap gap-3 items-start">
              <button type="button" onClick={goForm} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition cursor-pointer">戻る</button>
              <button
                type="button"
                onClick={submit}
                disabled={submitting}
                className={`px-6 py-2 rounded-lg transition shadow-md ${submitting ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer'}`}
              >
                {submitting ? '送信中...' : '送信'}
              </button>
            </div>
          </div>
        </div>
      );

      const sentBody = (
        <div className="max-w-3xl mx-auto px-6 py-16">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-3">送信しました。</h2>
            <p className="text-sm text-gray-600 mb-8">受付が完了しました。</p>
            <button
              type="button"
              onClick={() => {
                try { window.close(); } catch (e) { /* noop */ }
                setTimeout(() => {
                  try { window.location.replace('about:blank'); } catch (e) { /* noop */ }
                }, 50);
              }}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-md cursor-pointer"
            >閉じる</button>
          </div>
        </div>
      );

      const ErrorDialog = () => {
        if (!errorDialog) return null;
        return (
          <div
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 px-4"
            role="dialog"
            aria-modal="true"
          >
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-600 text-xl font-bold">!</div>
                <div className="flex-1 pt-1">
                  <h3 className="text-base font-bold text-gray-800 mb-2">エラー</h3>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">{errorDialog}</p>
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setErrorDialog('')}
                  className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-md cursor-pointer text-sm"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        );
      };

      const ToastContainer = () => (
        <div className="fixed bottom-6 left-1/2 z-[9999] flex -translate-x-1/2 flex-col-reverse items-center gap-2 pointer-events-none">
          {toastItems.map((item) => {
            const isError = item.type === 'error';
            return (
              <div
                key={item.id}
                className={`pointer-events-auto flex items-center gap-2 rounded-lg px-5 py-3 text-sm font-medium shadow-lg transition-all duration-300 ${
                  isError ? '' : 'cursor-pointer'
                } ${TOAST_TYPE_STYLES[item.type] || 'bg-gray-900 text-white'} ${item.exiting ? 'translate-y-4 opacity-0' : 'translate-y-0 opacity-100'}`}
                style={{ animation: item.exiting ? undefined : 'toastSlideUp 0.3s ease-out' }}
                onClick={isError ? undefined : () => dismissToast(item.id)}
                role="alert"
              >
                <span className="text-base leading-none">{TOAST_ICONS[item.type] || 'ℹ'}</span>
                <span>{item.message}</span>
                {isError && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      dismissToast(item.id);
                    }}
                    className="ml-2 flex h-5 w-5 items-center justify-center rounded text-base leading-none text-white/80 hover:bg-white/15 hover:text-white cursor-pointer"
                    aria-label="閉じる"
                  >
                    ×
                  </button>
                )}
              </div>
            );
          })}
        </div>
      );

      return (
        <div className="min-h-screen bg-gray-100 pb-12 text-sm text-gray-900">
          <header className="bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg no-print">
            <div className="max-w-4xl mx-auto px-6 py-5">
              <p className="text-sm text-blue-100">申出書情報</p>
              <h1 className="text-2xl font-bold">入力フォーム</h1>
            </div>
          </header>

          {page === 'basic' ? (
            basicBody
          ) : page === 'auth' ? (
            authBody
          ) : page === 'notice' ? (
            noticeBody
          ) : page === 'confirm' ? (
            confirmBody
          ) : page === 'sent' ? (
            sentBody
          ) : (
          <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">

            <div className="bg-white rounded-xl shadow-lg p-6 print-break">
              <h3 className="text-lg font-bold text-gray-800 mb-4">基本情報</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>識別ID</label>
                  <p className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-lg font-mono">{displayText(form.identId)}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 print-break">
              <h3 className="text-lg font-bold text-gray-800 mb-3">1. 申出者等</h3>
              <p className="text-sm text-gray-800 mb-4 leading-relaxed">{MEMBER_INSTRUCTION}</p>
              {showValidation && computeErrors().members ? (
                <div className="customer-inline-error mb-3">世帯員を1件以上入力してください（少なくとも1名の氏名が必要です）。</div>
              ) : null}
              <div className="space-y-4">
                {form.members.map((m, mi) => {
                  const memberErrs = showValidation ? (computeErrors().memberRow?.[mi] || {}) : {};
                  const memberFieldMsg = (k) => {
                    const err = memberErrs[k];
                    if (!err) return '';
                    if (k === 'furigana') return err === 'invalid' ? 'フリガナで入力してください。' : 'フリガナを入力してください。';
                    if (k === 'name') return '氏名を入力してください。';
                    if (k === 'phone') return err === 'required' ? '電話番号を入力してください。' : '電話番号の形式が正しくありません（10〜11桁の数字で入力してください）。';
                    if (k === 'email') return err === 'required' ? 'メールアドレスを入力してください。' : 'メールアドレスの形式が正しくありません。';
                    return '';
                  };
                  return (
                    <div key={mi} className="border border-gray-200 rounded-lg p-4 bg-gray-50/80">
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-sm font-medium text-gray-700">世帯員 {mi + 1}</span>
                        {form.members.length > 1 && mi > 0 ? (
                          <button type="button" onClick={() => removeMember(mi)} className="text-xs text-red-600 hover:underline cursor-pointer no-print">この欄を削除</button>
                        ) : null}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {memberTextFields.map(([k, lab, type, autoComplete]) => {
                          const msg = memberFieldMsg(k);
                          const isRequiredField = k === 'furigana' || k === 'name' || (k === 'phone' && mi === 0) || (k === 'email' && mi === 0);
                          return (
                            <div key={k}>
                              <label className={labelCls}>{isRequiredField ? requiredLabel(lab) : lab}</label>
                              <input
                                type={type}
                                value={m[k] ?? ''}
                                onChange={(e) => updateMember(mi, k, e.target.value)}
                                className={`${inputCls} ${k === 'phone' ? 'font-mono' : ''} ${memberErrs[k] ? errorBoxCls : ''}`}
                                autoComplete={autoComplete}
                                inputMode={k === 'phone' ? 'numeric' : undefined}
                              />
                              {msg ? <div className="customer-inline-error">{msg}</div> : null}
                            </div>
                          );
                        })}
                        <div>
                          <label className={labelCls}>{requiredLabel('世帯主との続柄')}</label>
                          <select
                            value={m.relationToHead ?? ''}
                            onChange={(e) => updateMemberRelation(mi, e.target.value)}
                            className={`${inputCls} ${mi === 0 ? 'bg-gray-100 text-gray-700 cursor-not-allowed' : ''} ${memberErrs.relationToHead ? errorBoxCls : ''}`}
                            disabled={mi === 0}
                            aria-readonly={mi === 0 ? 'true' : undefined}
                          >
                            <option value="">選択してください</option>
                            {RELATION_TO_HEAD_OPTIONS.map((option) => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                          {memberErrs.relationToHead ? (
                            <div className="customer-inline-error">世帯主との続柄を選択してください。</div>
                          ) : null}
                        </div>
                        {mi !== 0 && m.relationToHead === 'その他' ? (
                          <div>
                            <label className={labelCls}>{requiredLabel('続柄（その他）')}</label>
                            <input
                              type="text"
                              value={m.relationToHeadOther ?? ''}
                              onChange={(e) => updateMember(mi, 'relationToHeadOther', e.target.value)}
                              className={`${inputCls} ${memberErrs.relationToHeadOther ? errorBoxCls : ''}`}
                              placeholder="続柄を入力"
                            />
                            {memberErrs.relationToHeadOther ? (
                              <div className="customer-inline-error">続柄を入力してください。</div>
                            ) : null}
                          </div>
                        ) : null}
                        <div>
                          <label className={labelCls}>{requiredLabel('生年月日')}</label>
                          <input
                            type="date"
                            value={m.birthDate ?? ''}
                            onChange={(e) => updateMember(mi, 'birthDate', e.target.value)}
                            className={`${inputCls} ${memberErrs.birthDate ? errorBoxCls : ''}`}
                          />
                          {memberErrs.birthDate ? (
                            <div className="customer-inline-error">生年月日を入力してください。</div>
                          ) : null}
                        </div>
                        <div>
                          <label className={labelCls}>{requiredLabel('生活保護受給期間')}</label>
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                            <input
                              type="date"
                              value={m.benefitPeriodStartMonth ?? ''}
                              onChange={(e) => updateMember(mi, 'benefitPeriodStartMonth', e.target.value)}
                              className={`${inputCls} ${memberErrs.benefitPeriodStart ? errorBoxCls : ''}`}
                              aria-label="生活保護受給期間 開始日"
                            />
                            <span className="text-gray-500 text-center">～</span>
                            <input
                              type="date"
                              value={m.benefitPeriodEndMonth ?? ''}
                              onChange={(e) => updateMember(mi, 'benefitPeriodEndMonth', e.target.value)}
                              className={`${inputCls} ${memberErrs.benefitPeriodEnd ? errorBoxCls : ''}`}
                              aria-label="生活保護受給期間 終了日"
                            />
                          </div>
                          {memberErrs.benefitPeriodStart === 'required' ? (
                            <div className="customer-inline-error">生活保護受給期間（開始日）を入力してください。</div>
                          ) : null}
                          {memberErrs.benefitPeriodEnd === 'required' ? (
                            <div className="customer-inline-error">生活保護受給期間（終了日）を入力してください。</div>
                          ) : null}
                          {memberErrs.benefitPeriodEnd === 'order' ? (
                            <div className="customer-inline-error">終了日は開始日以降の日付を入力してください。</div>
                          ) : null}
                        </div>
                        <div>
                          <label className={labelCls}>死亡</label>
                          <label className="min-h-[38px] inline-flex items-center gap-2 text-sm text-gray-900">
                            <input
                              type="checkbox"
                              checked={m.death === 'maru'}
                              onChange={(e) => updateMember(mi, 'death', e.target.checked ? 'maru' : 'blank')}
                              className="w-4 h-4 text-blue-600"
                            />
                          </label>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <button type="button" onClick={addMember} className="mt-3 no-print inline-flex items-center gap-1 px-4 py-2 border border-dashed border-blue-400 text-blue-700 rounded-lg hover:bg-blue-50 text-sm cursor-pointer">
                <span className="text-lg leading-none">＋</span> 欄を追加
              </button>
              {MEMBER_FOOTNOTES}
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 print-break">
              <h3 className="text-lg font-bold text-gray-800 mb-3 leading-snug">2. 保護を受給していた当時の居所（選択肢をチェックしてください。）</h3>
              <p className="text-sm text-gray-800 mb-4">申出先の自治体で保護を受給されていた期間の居所が、</p>
              <div className={`flex flex-col gap-3 text-sm text-gray-900 ${showValidation && computeErrors().residenceRadio ? errorBoxCls + ' rounded-lg p-3' : ''}`}>
                <label className="inline-flex items-start gap-3 cursor-pointer">
                  <input type="radio" name="prs" checked={form.protectionResidenceSame === 'same'} onChange={() => setForm((prev) => ({ ...prev, protectionResidenceSame: 'same', pastAddresses: [] }))} className="mt-1 w-4 h-4 shrink-0 text-blue-600" />
                  <span>現住所と同じ</span>
                </label>
                <label className="inline-flex items-start gap-3 cursor-pointer">
                  <input type="radio" name="prs" checked={form.protectionResidenceSame === 'different'} onChange={() => updateField('protectionResidenceSame', 'different')} className="mt-1 w-4 h-4 shrink-0 text-blue-600" />
                  <span>現住所と異なる（異なる場合は以下の「当時の居所」及び「期間」を記載願います。）</span>
                </label>
              </div>

              {form.protectionResidenceSame === 'different' ? (
                <div className="mt-6 space-y-4">
                  <p className="text-sm font-medium text-gray-700">当時の居所・期間（最大3件）</p>
                  {showValidation && computeErrors().pastAddresses ? (
                    <div className="customer-inline-error">「当時の居所」と開始日・終了日を1件以上入力してください。終了日は開始日以降にしてください。</div>
                  ) : null}
                  {form.pastAddresses.map((p, pi) => (
                    <div key={pi} className="border border-gray-200 rounded-lg p-3 space-y-3 bg-white">
                      <div>
                        <label className={labelCls}>当時の居所</label>
                        <input
                          type="text"
                          value={p.address}
                          onChange={(e) => updatePastField(pi, 'address', e.target.value)}
                          placeholder="当時住んでいた場所を記載"
                          className={`${inputCls} ${showValidation && computeErrors().pastAddressRow && computeErrors().pastAddressRow[pi] && computeErrors().pastAddressRow[pi].address ? 'border-red-500 ring-2 ring-red-200' : ''}`}
                        />
                      </div>
                      <div>
                        <label className={labelCls}>期間</label>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                          <input
                            type="month"
                            value={p.periodStartMonth ?? ''}
                            onChange={(e) => updatePastField(pi, 'periodStartMonth', e.target.value)}
                            className={`${inputCls} ${showValidation && computeErrors().pastAddressRow && computeErrors().pastAddressRow[pi] && computeErrors().pastAddressRow[pi].period ? 'border-red-500 ring-2 ring-red-200' : ''}`}
                            aria-label="当時の居所 期間開始日"
                          />
                          <span className="text-gray-500 text-center">～</span>
                          <input
                            type="month"
                            value={p.periodEndMonth ?? ''}
                            onChange={(e) => updatePastField(pi, 'periodEndMonth', e.target.value)}
                            className={`${inputCls} ${showValidation && computeErrors().pastAddressRow && computeErrors().pastAddressRow[pi] && computeErrors().pastAddressRow[pi].period ? 'border-red-500 ring-2 ring-red-200' : ''}`}
                            aria-label="当時の居所 期間終了日"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">年月で入力してください。</p>
                      </div>
                      <button type="button" onClick={() => removePastAddress(pi)} className="no-print text-xs text-red-600 hover:underline cursor-pointer">この欄を削除</button>
                    </div>
                  ))}
                  {form.pastAddresses.length < 3 ? (
                    <button type="button" onClick={addPastAddress} className="no-print inline-flex items-center gap-1 px-4 py-2 border border-dashed border-blue-400 text-blue-700 rounded-lg hover:bg-blue-50 text-sm cursor-pointer">
                      <span className="text-lg leading-none">＋</span> 欄を追加
                    </button>
                  ) : null}
                </div>
              ) : (
                <p className="mt-4 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">「現住所と同じ」を選択中のため、当時の居所・期間の入力欄は表示していません。</p>
              )}
              {RESIDENCE_FOOTNOTES}
            </div>

            {(() => {
              const bankErrs = showValidation ? (computeErrors().bank || {}) : {};
              const bankFieldCls = (key) =>
                `rounded-lg border p-3 ${bankErrs[key] ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50/50'}`;
              const accountHolderMsg = bankErrs.accountHolderName === 'invalid'
                ? '半角カタカナ、半角スペース、全角スペースのみで入力してください。'
                : bankErrs.accountHolderName === 'required'
                  ? '口座名義（ｶﾀｶﾅ）を入力してください。'
                  : '';
              return (
                <div className="bg-white rounded-xl shadow-lg p-6 print-break">
                  <h3 className="text-lg font-bold text-gray-800 mb-2">3. 口座情報</h3>
                  <p className="text-sm text-gray-800 mb-4 leading-relaxed">以下の記入に加え、これらの口座情報が確認できる通帳等の写しを添付してください。</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                    <div className={bankFieldCls('bankName')}>
                      <label className={labelCls}>{requiredLabel('銀行番号')}</label>
                      <input
                        type="text"
                        value={form.bank.bankName}
                        onChange={(e) => updateBank('bankName', e.target.value)}
                        inputMode="numeric"
                        maxLength={4}
                        className={`${inputCls} font-mono ${bankErrs.bankName ? errorBoxCls : ''}`}
                      />
                      {bankErrs.bankName ? (
                        <div className="customer-inline-error">
                          {bankErrs.bankName === 'format' ? '半角数字4桁で入力してください。' : '銀行番号を入力してください。'}
                        </div>
                      ) : null}
                    </div>
                    <div className={bankFieldCls('branchName')}>
                      <label className={labelCls}>{requiredLabel('支店番号')}</label>
                      <input
                        type="text"
                        value={form.bank.branchName}
                        onChange={(e) => updateBank('branchName', e.target.value)}
                        inputMode="numeric"
                        maxLength={3}
                        className={`${inputCls} font-mono ${bankErrs.branchName ? errorBoxCls : ''}`}
                      />
                      {bankErrs.branchName ? (
                        <div className="customer-inline-error">
                          {bankErrs.branchName === 'format' ? '半角数字3桁で入力してください。' : '支店番号を入力してください。'}
                        </div>
                      ) : null}
                    </div>
                    <div className={bankFieldCls('accountType')}>
                      <label className={labelCls}>{requiredLabel('預金種目')}</label>
                      <select
                        value={form.bank.accountType}
                        onChange={(e) => updateBank('accountType', e.target.value)}
                        className={`${inputCls} ${bankErrs.accountType ? errorBoxCls : ''}`}
                      >
                        <option value="1">普通預金</option>
                        <option value="2">当座預金</option>
                        <option value="4">貯蓄</option>
                        <option value="9">その他</option>
                      </select>
                      {bankErrs.accountType ? (
                        <div className="customer-inline-error">預金種目を選択してください。</div>
                      ) : null}
                    </div>
                    <div className={bankFieldCls('accountNumber')}>
                      <label className={labelCls}>{requiredLabel('口座番号')}</label>
                      <input
                        type="text"
                        value={form.bank.accountNumber}
                        onChange={(e) => updateBank('accountNumber', e.target.value)}
                        inputMode="numeric"
                        maxLength={7}
                        className={`${inputCls} font-mono ${bankErrs.accountNumber ? errorBoxCls : ''}`}
                      />
                      {bankErrs.accountNumber ? (
                        <div className="customer-inline-error">
                          {bankErrs.accountNumber === 'format' ? '半角数字1〜7桁で入力してください。' : '口座番号を入力してください。'}
                        </div>
                      ) : null}
                    </div>
                    <div className={bankFieldCls('accountHolderName')}>
                      <label className={labelCls}>{requiredLabel('口座名義')}</label>
                      <input
                        type="text"
                        value={form.bank.accountHolderName}
                        onChange={(e) => updateBank('accountHolderName', e.target.value)}
                        placeholder="例：ﾔﾏﾀﾞ ﾀﾛｳ"
                        className={`${inputCls} ${bankErrs.accountHolderName ? errorBoxCls : ''}`}
                      />
                      {accountHolderMsg ? (
                        <div className="customer-inline-error">{accountHolderMsg}</div>
                      ) : null}
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-gray-800">※ 振込口座の名義は、申出者本人名義のものに限ります。</p>
                </div>
              );
            })()}

            <div className="bg-white rounded-xl shadow-lg p-6 print-break">
              <h3 className="text-lg font-bold text-gray-800 mb-2 leading-snug">4. 申出者以外の世帯員の連絡先（複数人世帯の場合）</h3>
              <p className="text-sm text-gray-800 mb-4 leading-relaxed">複数人世帯の場合は、申出者へ連絡がつかないときの連絡先（他の世帯員）を記載してください。申出内容の確認等のため、必要に応じてご連絡することがあります。</p>
              {(() => {
                const rows = otherHouseholdContactsOf(form);
                const rowErrors = showValidation ? (computeErrors().otherHouseholdMembers || []) : [];
                return (
                  <div className="space-y-3">
                    {rows.map((row, oi) => {
                      const ohmErrs = rowErrors[oi] || {};
                      const phoneErrCls = ohmErrs.phone ? errorBoxCls : '';
                      return (
                        <div key={oi} className="border border-gray-200 rounded-lg p-4 space-y-3">
                          <div className="flex items-center justify-between gap-3">
                            <div className="text-sm font-medium text-gray-700">#{oi + 1}</div>
                            {rows.length > 1 ? (
                              <button type="button" onClick={() => removeOtherHouseholdMember(oi)} className="text-sm text-red-600 hover:underline cursor-pointer">削除</button>
                            ) : null}
                          </div>
                          <div>
                            <label className={labelCls}>他の世帯員の氏名</label>
                            <input type="text" value={row?.name ?? ''} onChange={(e) => updateOther(oi, 'name', e.target.value)} className={inputCls} />
                          </div>
                          <div>
                            <label className={labelCls}>電話番号（携帯または自宅）</label>
                            <input
                              type="tel"
                              inputMode="numeric"
                              value={otherHouseholdPhoneDigits(row)}
                              onChange={(e) => updateOther(oi, 'phone', e.target.value)}
                              className={`${inputCls} font-mono ${phoneErrCls}`}
                              autoComplete="tel"
                            />
                            {ohmErrs.phone ? (
                              <div className="customer-inline-error">電話番号の形式が正しくありません（10〜11桁の数字で入力してください）。</div>
                            ) : null}
                          </div>
                          <div>
                            <label className={labelCls}>メールアドレス</label>
                            <input
                              type="email"
                              value={row?.email ?? ''}
                              onChange={(e) => updateOther(oi, 'email', e.target.value)}
                              className={`${inputCls} ${ohmErrs.email ? errorBoxCls : ''}`}
                              autoComplete="email"
                            />
                            {ohmErrs.email ? (
                              <div className="customer-inline-error">メールアドレスの形式が正しくありません。</div>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                    {rows.length < 2 ? (
                      <button type="button" onClick={addOtherHouseholdMember} className="no-print px-4 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-800 hover:bg-gray-50 cursor-pointer">＋ 欄を追加</button>
                    ) : null}
                  </div>
                );
              })()}
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 print-break">
              <h3 className="text-lg font-bold text-gray-800 mb-2">5. 加算等の申出</h3>
              {ADDITION_INTRO}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="block md:table w-full text-sm border-collapse">
                  <thead className="hidden md:table-header-group">
                    <tr>
                      <th className="border border-gray-300 bg-gray-100 px-2 py-2 text-left text-xs font-semibold text-gray-800 align-middle w-[20%]">申出に係る加算等の種類</th>
                      <th className="border border-gray-300 bg-gray-100 px-2 py-2 text-left text-xs font-semibold text-gray-800 align-middle">該当する内容（平成 25 年８月から令和８年３月までのうち、該当がある場合に記載ください。）</th>
                      <th className="border border-gray-300 bg-gray-100 px-2 py-2 text-left text-xs font-semibold text-gray-800 align-middle w-[22%]">挙証資料の例</th>
                      <th className="border border-gray-300 bg-gray-100 px-2 py-2 text-left text-xs font-semibold text-gray-800 align-middle w-[22%]">添付（画像）</th>
                    </tr>
                  </thead>
                  <tbody className="block md:table-row-group">
                    {form.additionRows.length === 0 ? (
                      <tr className="block md:table-row">
                        <td colSpan={4} className="block md:table-cell border border-gray-300 px-4 py-6 text-center text-gray-500 text-sm">下の「欄を追加」から入力欄を追加してください。</td>
                      </tr>
                    ) : (
                      form.additionRows.map((a, ai) => (
                        <tr key={ai} className="block md:table-row border-b border-gray-200 md:border-b-0 last:border-b-0">
                          <td className="block md:table-cell border-b md:border border-gray-300 p-2 align-top bg-white">
                            <div className="md:hidden text-xs font-semibold text-gray-700 mb-1">申出に係る加算等の種類</div>
                            <select value={a.category} onChange={(e) => updateAddition(ai, 'category', e.target.value)} className="w-full border border-gray-200 rounded px-2 py-1.5 text-xs bg-white">
                              {APP_DOC_ADDON_TYPES.map((t) => (<option key={t} value={t}>{t}</option>))}
                            </select>
                            <button type="button" onClick={() => removeAdditionRow(ai)} className="no-print mt-2 text-xs text-red-600 hover:underline cursor-pointer">この欄を削除</button>
                          </td>
                          <td className="block md:table-cell border-b md:border border-gray-300 p-2 align-top bg-white">
                            <div className="md:hidden text-xs font-semibold text-gray-700 mb-1">該当する内容（平成 25 年８月から令和８年３月までのうち、該当がある場合に記載ください。）</div>
                            <textarea value={a.content} onChange={(e) => updateAddition(ai, 'content', e.target.value)} rows={3} className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm resize-y min-h-[4.5rem]" placeholder="例：○○病院に平成26年4月～平成27年3月まで入院" />
                            <p className="mt-1 text-xs text-gray-500 leading-relaxed">入院期間、病院名、加算対象期間などを記載してください。</p>
                          </td>
                          <td className="block md:table-cell border-b md:border border-gray-300 p-2 align-top bg-white text-sm text-gray-800">
                            <div className="md:hidden text-xs font-semibold text-gray-700 mb-1">挙証資料の例</div>
                            <div className="whitespace-pre-line leading-relaxed">{evidenceExampleByCategory(a.category)}</div>
                          </td>
                          <td className="block md:table-cell md:border border-gray-300 p-2 align-top bg-white">
                            <div className="md:hidden text-xs font-semibold text-gray-700 mb-1">添付（画像）</div>
                            <div className="flex flex-col gap-2">
                              {(() => {
                                const uploads = Array.isArray(a.uploads) && a.uploads.length
                                  ? a.uploads
                                  : (a.upload && (a.upload.previewUrl || a.upload.name) ? [a.upload] : []);
                                return (
                                  <>
                                    <div className="flex flex-wrap items-center gap-2">
                                      <label className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded border border-gray-300 bg-white hover:bg-gray-50 transition cursor-pointer">
                                        {uploads.length > 0 ? 'ファイルを追加' : 'ファイル選択'}
                                        <input
                                          type="file"
                                          accept={ATTACHMENT_FILE_ACCEPT}
                                          multiple
                                          onChange={(e) => {
                                            setAdditionUploadsFromFiles(ai, e.target.files);
                                            e.target.value = '';
                                          }}
                                          className="hidden"
                                        />
                                      </label>
                                      {uploads.length > 0 ? (
                                        <span className="text-xs text-gray-600">ファイル：{uploads.length}件</span>
                                      ) : (
                                        <span className="text-xs text-gray-500">PDF / JPG / JPEG / PNG</span>
                                      )}
                                    </div>
                                    {uploads.length === 0 ? (
                                      <div className="text-xs text-gray-500 leading-relaxed">選択した画像またはPDFのプレビューがここに表示されます。</div>
                                    ) : (
                                      <div className="grid grid-cols-1 gap-2">
                                        {uploads.map((u, ui) => {
                                          const pdf = u.isPdf || isPdfFile(u.name);
                                          return (
                                            <div key={ui} className="flex items-start gap-2">
                                              {pdf ? (
                                                <div className="flex items-center gap-2 border border-gray-200 rounded p-2 bg-gray-50 flex-1 min-w-0">
                                                  <span className="text-2xl">📄</span>
                                                  <span className="text-xs text-gray-600 break-all">{u.name}</span>
                                                </div>
                                              ) : u.previewUrl ? (
                                                <div className="flex flex-col gap-1 flex-1 min-w-0">
                                                  <img src={u.previewUrl} alt="添付プレビュー" className="w-full max-h-44 object-contain border border-gray-200 rounded bg-gray-50" />
                                                  {u.name ? (<div className="text-[11px] text-gray-600 break-all">{u.name}</div>) : null}
                                                </div>
                                              ) : (
                                                <div className="flex items-center gap-2 border border-gray-200 rounded p-2 bg-gray-50 flex-1 min-w-0">
                                                  <span className="text-xs text-gray-600 break-all">{u.name || '(無名)'}</span>
                                                </div>
                                              )}
                                              <button
                                                type="button"
                                                onClick={() => removeAdditionUpload(ai, ui)}
                                                className="no-print text-xs text-red-600 hover:underline cursor-pointer shrink-0 mt-1"
                                              >
                                                削除
                                              </button>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <button type="button" onClick={addAdditionRow} className="mt-3 no-print inline-flex items-center gap-1 px-4 py-2 border border-dashed border-blue-400 text-blue-700 rounded-lg hover:bg-blue-50 text-sm cursor-pointer">
                <span className="text-lg leading-none">＋</span> 欄を追加
              </button>

              <div className="mt-6">
                {CLOSING_NOTES}
              </div>
            </div>

            {RYUI_JIKO}

            <div className={`bg-white rounded-xl shadow-lg p-6 print-break ${showValidation && computeErrors().attachments ? errorBoxCls : ''}`}>
              <h3 className="text-lg font-bold text-gray-800 mb-2">添付書類（証明書等）</h3>
              <p className="text-sm text-gray-600 mb-4">戸籍謄本・住民票・戸籍の附票・代理人関係書類のほか、その他の証明書についても、各書類ごとに最大 {MAX_FILES_PER_ATTACHMENT} 件までアップロードできます。ファイル形式は PDF / JPG / JPEG / PNG、1 ファイルあたり 5MB 以内です。プレビューは「表示」クリック時のみ開きます。</p>
              {showValidation && computeErrors().attachments ? (
                <div className="customer-inline-error mb-3">必須提出書類に対応する証明書（ファイル）の添付が不足しています。</div>
              ) : null}
              <div className="space-y-4">
                {APP_DOC_ATTACHMENT_DEFS.map((def) => {
                  const parts = (def.parts && def.parts.length ? def.parts : [{ key: 'single', label: '' }]);
                  const missing =
                    showValidation &&
                    computeErrors().missingAttachmentKeys &&
                    Array.isArray(computeErrors().missingAttachmentKeys) &&
                    computeErrors().missingAttachmentKeys.includes(def.key);
                  return (
                    <div key={def.key} className={`border rounded-lg p-3 bg-gray-50/50 ${missing ? 'border-red-500 ring-2 ring-red-200' : 'border-gray-200'}`}>
                      <div className="text-sm font-medium text-gray-800 mb-2 leading-snug whitespace-pre-line">{def.label}</div>
                      {missing && ATTACHMENT_REQUIRED_MESSAGES[def.key] ? (
                        <div className="customer-inline-error mb-2">{ATTACHMENT_REQUIRED_MESSAGES[def.key]}</div>
                      ) : null}
                      <div className="space-y-3">
                        {parts.map((p) => {
                          const part = getAttPart(form.attachments, def.key, p.key);
                          const partImages = Array.isArray(part.images) ? part.images : [];
                          const previewId = `${def.key}:${p.key}`;
                          const hideFileSelect = isSingleFilePartAttachment(def.key, p.key) && partImages.length > 0;
                          return (
                            <div key={p.key} className="border border-gray-200 rounded-lg bg-white p-3">
                              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                {p.label ? <div className="text-sm font-medium text-gray-700">{p.label}</div> : <div />}
                                <div className="flex items-center gap-2">
                                  {!hideFileSelect ? (
                                    <label className="inline-flex items-center px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 transition cursor-pointer">
                                      ファイル選択
                                      <input
                                        type="file"
                                        accept={ATTACHMENT_FILE_ACCEPT}
                                        multiple
                                        className="hidden"
                                        onChange={(e) => {
                                          setAttachmentImagesFromFiles(def.key, p.key, e.target.files);
                                          e.target.value = '';
                                        }}
                                      />
                                    </label>
                                  ) : null}
                                  <button
                                    type="button"
                                    onClick={() => setOpenPreview((prev) => (prev === previewId ? '' : previewId))}
                                    className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 transition cursor-pointer"
                                  >
                                    {openPreview === previewId ? '非表示' : '表示'}
                                  </button>
                                </div>
                              </div>
                              <div className="text-sm text-gray-800">{attachmentSummary(part)}</div>
                              {openPreview === previewId ? (
                                <div className="mt-3">
                                  {Array.isArray(part.images) && part.images.length ? (
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                      {part.images.map((img, idx) => {
                                        const pdf = img.isPdf || isPdfFile(img.fileName);
                                        return (
                                          <div key={idx} className="relative flex flex-col gap-1">
                                            {pdf ? (
                                              <div className="flex items-center gap-2 border border-gray-200 rounded p-2 bg-gray-50 w-full h-32">
                                                <span className="text-2xl">📄</span>
                                                <span className="text-xs text-gray-600 break-all">{img.fileName}</span>
                                              </div>
                                            ) : (
                                              <img src={img.previewUrl} alt="添付プレビュー" className="w-full h-32 object-contain border border-gray-200 rounded bg-white" />
                                            )}
                                            <div className="flex items-center justify-between gap-2">
                                              <span className="text-[11px] text-gray-600 break-all flex-1 min-w-0">{img.fileName}</span>
                                              <button
                                                type="button"
                                                onClick={() => removeAttachmentImage(def.key, p.key, idx)}
                                                className="no-print text-xs text-red-600 hover:underline cursor-pointer shrink-0"
                                              >
                                                削除
                                              </button>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <div className="text-sm text-gray-500">ファイルのプレビューはありません。</div>
                                  )}
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 print-break">
              <h3 className="text-lg font-bold text-gray-800 mb-4 text-center">同意書</h3>
              {showValidation && computeErrors().consentForm ? (
                <div className="customer-inline-error mb-3">同意書の記入（年月日・住所・氏名）は必須です。</div>
              ) : null}
              <div className={`text-sm text-gray-900 leading-relaxed space-y-4 ${showValidation && computeErrors().consentForm ? errorBoxCls + ' rounded-lg p-4' : ''}`}>
                <p className="text-right text-gray-700">福祉事務所長　殿</p>
                <p className="whitespace-pre-line">
                  {`平成 25 年生活扶助基準改定に関する最高裁判決を踏まえた保護費の追加給付の決定のために必要があるときは、私及び私の世帯員（以下「私等」という。）の保護の受給状況を確認するため、貴福祉事務所が官公署、本人口座機関等（以下「官公署等」という。）に対し、必要な書類の閲覧若しくは資料の提供を求めることに同意します。\nまた、貴福祉事務所の調査又は報告要求に対し、官公署等が報告することについて、私等が同意している旨を官公署等に伝えて構いません。`}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-4">
                  <div>
                    <label className={labelCls}>{requiredLabel('年月日')}</label>
                    <input
                      type="date"
                      value={consentDateValue(form.consentForm)}
                      onChange={(e) => updateConsentDate(e.target.value)}
                      className={inputCls}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
                  <div><label className={labelCls}>{requiredLabel('住所')}</label><input type="text" value={form.consentForm?.address ?? ''} onChange={(e) => updateField('consentForm', { ...(form.consentForm || {}), address: e.target.value })} className={inputCls} /></div>
                  <div><label className={labelCls}>{requiredLabel('氏名')}</label><input type="text" value={form.consentForm?.name ?? ''} onChange={(e) => updateField('consentForm', { ...(form.consentForm || {}), name: e.target.value })} className={inputCls} /></div>
                </div>
              </div>
            </div>

            <div className={`bg-white rounded-xl shadow-lg p-6 print-break ${showValidation && computeErrors().checklist ? errorBoxCls : ''}`}>
              <h3 className="text-lg font-bold text-gray-800 mb-2">申出に当たってのチェックリスト</h3>
              {showValidation && computeErrors().checklist ? (
                <div className="customer-inline-error mb-3">必須のチェック項目にすべてチェックを入れてください。（外国人の場合の項目・必要に応じて提出いただく書類は、該当しない場合は未チェックのままで構いません。）</div>
              ) : (
                <p className="text-sm text-gray-600 mb-4">
                  必須項目にチェックを入れてから、入力確認画面へお進みください。外国人の場合の住民票等、および「必要に応じて提出いただく資料」は、該当する場合のみチェックしてください。
                </p>
              )}
              <div className="text-sm text-gray-900 leading-relaxed space-y-6">
                <div>
                  <p className="font-bold mb-2">① 申出書に添付する書類</p>
                  <p className="text-sm text-gray-800 mb-3">下記が全て添付されているかチェックの上、申出をお願いいたします。（電子申出（マイナポータル等）の場合は写真を添付、郵送の場合は写しを添付願います。）</p>
                  <div className="border border-gray-300 rounded-lg p-4">
                    <p className="font-semibold mb-2">【必ずご提出いただく資料】</p>
                    <div className="space-y-2">
                      <label className="flex gap-2 items-start">
                        <input type="checkbox" className="mt-1" checked={!!form.requiredDocs?.idPhoto} onChange={(e) => updateRequiredDoc('idPhoto', e.target.checked)} />
                        <span>以下のいずれか一つ（申出者の顔写真付きの本人確認書類）：マイナンバーカードの写し（番号の記載がない両面）、運転免許証の写し、在留カードの写し、障害者手帳の写し、パスポートの写し等</span>
                      </label>
                      <label className="flex gap-2 items-start">
                        <input type="checkbox" className="mt-1" checked={!!form.requiredDocs?.kosekiAll} onChange={(e) => updateRequiredDoc('kosekiAll', e.target.checked)} />
                        <span>全世帯員の戸籍謄本の写し（全部事項証明書）</span>
                      </label>
                      <div className="pl-6 text-sm text-gray-800 space-y-1">
                        <p>※ 当時の姓が現在と異なる場合は、併せて当時の姓が記載された戸籍謄本を添付してください。</p>
                        <p>※ 離婚等により、当時の世帯員が申出者の戸籍から除籍となっている場合、申出者において戸籍の第三者請求により、除籍となった世帯員の現在の戸籍謄本を請求することができます。</p>
                        <p>※ 保護廃止中の場合は、戸籍謄本に代えて保護受給証明書によることも可</p>
                      </div>
                      <label className="flex gap-2 items-start">
                        <input type="checkbox" className="mt-1" checked={!!form.requiredDocs?.juminhyoForeign} onChange={(e) => updateRequiredDoc('juminhyoForeign', e.target.checked)} />
                        <span>
                          <span className="block">全世帯員の住民票の写し（外国人の場合）（※保護受給中の場合は住民票に代えて保護受給証明書によることも可）</span>
                          <span className="block text-xs text-gray-600 mt-1">外国人に該当しない場合は、未チェックのままで構いません。</span>
                        </span>
                      </label>
                      <label className="flex gap-2 items-start">
                        <input type="checkbox" className="mt-1" checked={!!form.requiredDocs?.bankProof} onChange={(e) => updateRequiredDoc('bankProof', e.target.checked)} />
                        <span>申出者本人の「3. 口座情報」に記載の口座情報が確認できる通帳の写し、またはキャッシュカードの写し</span>
                      </label>
                    </div>
                    <div className="mt-4">
                      <p className="font-semibold mb-1">【必要に応じて提出いただく資料】</p>
                      <p className="text-xs text-gray-600 mb-2">該当する場合のみチェック（必須ではありません）</p>
                      <div className="space-y-2">
                        <label className="flex gap-2 items-start">
                          <input type="checkbox" className="mt-1" checked={!!form.requiredDocs?.addonEvidence} onChange={(e) => updateRequiredDoc('addonEvidence', e.target.checked)} />
                          <span>「5. 加算等の申出」で必要とされる挙証資料</span>
                        </label>
                        <label className="flex gap-2 items-start">
                          <input type="checkbox" className="mt-1" checked={!!form.requiredDocs?.kosekiFupyo} onChange={(e) => updateRequiredDoc('kosekiFupyo', e.target.checked)} />
                          <span>当時の居所を記載できない場合は、戸籍謄本の附票の写し</span>
                        </label>
                        <label className="flex gap-2 items-start">
                          <input type="checkbox" className="mt-1" checked={!!form.requiredDocs?.proxyDocs} onChange={(e) => updateRequiredDoc('proxyDocs', e.target.checked)} />
                          <span>代理による申出を行う場合は、委任状、身分確認書類、本人との関係を証する書類</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <p className="font-bold mb-2">② 記載事項</p>
                  <p className="text-sm text-gray-800 mb-3">下記が全て添付されているかチェックの上、申出をお願いいたします。（電子申出（マイナポータル等）の場合は写真を添付、郵送の場合は写しを添付願います。）</p>
                  <div className="border border-gray-300 rounded-lg p-4 space-y-2">
                    <label className="flex gap-2 items-start">
                      <input type="checkbox" className="mt-1" checked={!!form.requiredDocs?.headAtTime} onChange={(e) => updateRequiredDoc('headAtTime', e.target.checked)} />
                      <span>申出者が保護受給当時の世帯主（当時の世帯主が死亡している場合は、当時の世帯主に準ずる者）となっているか</span>
                    </label>
                    <label className="flex gap-2 items-start">
                      <input type="checkbox" className="mt-1" checked={!!form.requiredDocs?.membersListed} onChange={(e) => updateRequiredDoc('membersListed', e.target.checked)} />
                      <span>申出先の自治体で保護を受給されていた期間の世帯員が記載されているか（当該期間に世帯内にいない方は追加給付の対象外となります）</span>
                    </label>
                    <label className="flex gap-2 items-start">
                      <input type="checkbox" className="mt-1" checked={!!form.requiredDocs?.bankNameMatch} onChange={(e) => updateRequiredDoc('bankNameMatch', e.target.checked)} />
                      <span>振込口座が申出者本人の名義となっているか</span>
                    </label>
                    <label className="flex gap-2 items-start">
                      <input type="checkbox" className="mt-1" checked={!!form.requiredDocs?.additionDetails} onChange={(e) => updateRequiredDoc('additionDetails', e.target.checked)} />
                      <span>加算等の該当がある場合、該当する方の氏名や該当期間が記載されているか</span>
                    </label>
                    <label className="flex gap-2 items-start">
                      <input type="checkbox" className="mt-1" checked={!!form.requiredDocs?.contactListed} onChange={(e) => updateRequiredDoc('contactListed', e.target.checked)} />
                      <span>連絡先が記載されているか</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-10">
              <div className={`text-sm text-gray-700 ${showValidation && computeErrors().privacy ? errorBoxCls + ' rounded-lg p-3' : ''}`}>
                <label className="inline-flex items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={hasPrivacyPolicyUrl && privacyAgreed}
                    disabled={!hasPrivacyPolicyUrl}
                    onChange={(e) => setPrivacyAgreed(hasPrivacyPolicyUrl && e.target.checked)}
                  />
                  <span>
                    {hasPrivacyPolicyUrl ? (
                      <a
                        href={normalizedPrivacyPolicyUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-700 hover:underline"
                      >
                        プライバシーポリシー
                      </a>
                    ) : (
                      <span className="font-medium text-red-600">プライバシーポリシー</span>
                    )}{' '}
                    に同意します。
                  </span>
                </label>
              </div>
              <button
                type="button"
                onClick={goConfirm}
                disabled={!hasPrivacyPolicyUrl || !privacyAgreed}
                title={!hasPrivacyPolicyUrl ? 'プライバシーポリシーURLが未設定です' : !privacyAgreed ? 'プライバシーポリシーに同意してください' : undefined}
                className={`px-6 py-2 rounded-lg transition shadow-md ${
                  hasPrivacyPolicyUrl && privacyAgreed ? 'bg-blue-600 text-white hover:bg-blue-700 cursor-pointer' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                入力確認画面へ
              </button>
            </div>
          </div>
          )}
          <ToastContainer />
          <ErrorDialog />
        </div>
      );
    }

export default ApplicationFormFormat;
